import React, { useState, useEffect, useMemo, useCallback } from "react";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  StandardizedTable,
  TableColumn,
  TrendIndicator,
  type TableRow,
} from "@/components/ui/standardized-table";
import { HelpTooltip } from "@/components/ui/help-tooltip";
import { rodapeDoConjunto } from "@/lib/rodape-do-conjunto";
import {
  linhaDeCidade,
  linhaDeDispositivo,
  linhaDeEstado,
  linhaDeGenero,
  linhaDeGrupoDeCanais,
  linhaDeIdade,
} from "@/lib/linhas-da-taxa-de-conversao";
import { KpiNumber } from "@/components/ui/kpi-number";
import { Button } from "@/components/ui/button";
import GlobalFilter from "@/components/common/GlobalFilter";
import CampaignFiltersControl from "@/components/common/CampaignFiltersControl";
import { useGlobalFilter } from "@/features/filters/filter.context";
import { formatDateToISO } from "@/features/filters/filter.types";
import { isComparisonEnabled } from "@/features/filters/filter.utils";
import { useAccount } from "@/contexts/account.context";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  LineChart as TremorLineChart,
  DonutChart as TremorDonutChart,
} from "@tremor/react";
import { ScrollableChart } from "@/components/ui/scrollable-chart";
import {
  getGoogleAnalyticsConversionRateCardsKpisApiV1GoogleAnalyticsConversionRateCardsKpisGet,
  getConversionRateFullApiV1ConversionRateFullGet,
  getConversionRateUtmCampaignsApiV1ConversionRateUtmCampaignsGet,
  getProductsRevenueTrendApiV1ProductsRevenueTrendGet,
  getConversionRateDemographicsV2ApiV1ConversionRateDemographicsV2Get,
  getAcquisitionTimelineApiV1AcquisitionTimelineGet,
  getRegionsSalesByStateApiV1RegionsSalesByStateGet,
  getRegionsSalesByCityApiV1RegionsSalesByCityGet,
  getDevicesSalesApiV1DevicesSalesGet,
  getAcquisitionChannelGroupingApiV1AcquisitionChannelGroupingGet,
} from "@/backend-sdk/client/sdk.gen";
import type { ConversionRateCardsKpisResponse } from "@/backend-sdk/client/types.gen";
import { Skeleton } from "@/components/ui/skeleton";
import { CarregandoConteudo } from "@/components/ui/carregando-conteudo";
import {
  CarregandoValor,
  EstadoDoValor,
  ValorIndisponivel,
} from "@/components/ui/estado-do-valor";
import type { RequisitoDeFonte } from "@/lib/motivo-do-indisponivel";
import { formatPercent, formatInteger } from "@/utils/numberFormat";
import { formatDateBR } from "@/utils/dateFormat";
import CategoriasMaisVendidasTable from "@/components/dashboard/CategoriasMaisVendidasTable";
import { getKpiTooltip } from "@/lib/kpi-tooltips";
import {
  DrilldownProvider,
  useDrilldownOptional,
  type DrillDimension,
} from "@/features/drilldown/drilldown.context";
import { ActiveFiltersDisplay } from "@/features/drilldown/ActiveFiltersDisplay";
import useCampaignFiltersState from "@/hooks/useCampaignFiltersState";

// Props de cross-filtering para tabelas inline
interface DrilldownTableProps {
  onRowClick?: (row: TableRow, index: number, event?: React.MouseEvent) => void;
  isRowSelected?: (row: TableRow, index: number) => boolean;
  clickableRows?: boolean;
}

type ItemNameOperator = "equals" | "contains" | "starts_with" | "regex" | "in";

interface PaginatedRows {
  items: TableRow[];
  pagination: TableRow;
  /** <M-230> O "Total Geral" que a rota publica — nulo quando a tabela está vazia. */
  total_row?: TableRow | null;
}

interface RegionTableData {
  items: TableRow[];
  pagination?: { page: number; page_size: number; total: number };
  /** <M-230> O "Total Geral" da rota, já passado pelo guarda de `rodapeDoConjunto`. */
  totalRow: TableRow | null;
}

interface DevCharts {
  chart: TableRow[];
  table: TableRow[];
  /** <M-230> Idem — aqui o guarda olha a lista, porque esta rota não pagina. */
  totalRow: TableRow | null;
}

interface DemographicsData {
  ages?: PaginatedRows;
  genders?: PaginatedRows;
}

// OBS: mockData removido — página consome dados reais do backend
/* const mockData = {
  cards: {
    receitaTotal: { value: 45230, change: 12.5, trend: 'up' as const },
    pedidos: { value: 1234, change: 8.2, trend: 'up' as const },
    ticketMedio: { value: 36.70, change: -2.1, trend: 'down' as const },
    taxaConversao: { value: 3.2, change: 5.8, trend: 'up' as const },
    totalUsuarios: { value: 15678, change: 15.3, trend: 'up' as const },
    sessoes: { value: 28945, change: 22.1, trend: 'up' as const },
    sessoesEngajadas: { value: 18234, change: 18.7, trend: 'up' as const },
    txEngajamento: { value: 63.0, change: 4.2, trend: 'up' as const }
  },
  
  // Dados para o gráfico de taxa de conversão
  evolucaoTaxaConversao: [
    { data: '16 de ago.', taxaConversao: 1.1, taxaConversaoAnterior: 1.2 },
    { data: '17 de ago.', taxaConversao: 0.8, taxaConversaoAnterior: 1.3 },
    { data: '18 de ago.', taxaConversao: 1.1, taxaConversaoAnterior: 1.2 },
    { data: '19 de ago.', taxaConversao: 0.9, taxaConversaoAnterior: 1.1 },
    { data: '20 de ago.', taxaConversao: 1.2, taxaConversaoAnterior: 1.2 },
    { data: '21 de ago.', taxaConversao: 0.8, taxaConversaoAnterior: 1.1 },
    { data: '22 de ago.', taxaConversao: 0.7, taxaConversaoAnterior: 1.0 }
  ],

  // ... removido ...

  // Dados para produtos mais vendidos
  // ... removido ...

  // Dados para conversão por origem/mídia
  // ... removido ...

  // Dados para conversão por campanha (UTM)
  conversaoPorCampanha: [
    { campanha: '[PESQUISA] [AB] Institucional', txConversao: 2.54, change: -11.9, trend: 'down' as const, receitaTotal: 23411.15, changeReceita: -33.1, trendReceita: 'down' as const },
    { campanha: 'lojaonline', txConversao: 1.84, change: -17.0, trend: 'down' as const, receitaTotal: 17521.49, changeReceita: -29.2, trendReceita: 'down' as const },
    { campanha: '(direct)', txConversao: 0.73, change: -25.7, trend: 'down' as const, receitaTotal: 10430.38, changeReceita: -51.4, trendReceita: 'down' as const },
    { campanha: '(organic)', txConversao: 1.82, change: 3.1, trend: 'up' as const, receitaTotal: 9352.71, changeReceita: 18.6, trendReceita: 'up' as const },
    { campanha: '[M] [TESTE] [PMAX] [AB] categoria Shorts', txConversao: 3.65, change: 38.8, trend: 'up' as const, receitaTotal: 4273.16, changeReceita: 54.8, trendReceita: 'up' as const }
  ],

  // Dados para conversão por estado
  conversaoPorEstado: [
    { estado: 'State of São Paulo', txConversao: 1.18, change: -0.8, trend: 'down' as const, receitaTotal: 56929.19, changeReceita: -15.7, trendReceita: 'down' as const },
    { estado: 'State of Minas Gerais', txConversao: 0.83, change: -20.7, trend: 'down' as const, receitaTotal: 13323.08, changeReceita: -2.7, trendReceita: 'down' as const },
    { estado: 'State of Paraná', txConversao: 0.95, change: -18.9, trend: 'down' as const, receitaTotal: 10214.26, changeReceita: -23.3, trendReceita: 'down' as const },
    { estado: 'State of Santa Catarina', txConversao: 1.13, change: -30.2, trend: 'down' as const, receitaTotal: 8371.33, changeReceita: -41.6, trendReceita: 'down' as const },
    { estado: 'State of Rio de Janeiro', txConversao: 0.64, change: -43.9, trend: 'down' as const, receitaTotal: 7599.26, changeReceita: -62.3, trendReceita: 'down' as const }
  ],

  // Dados para conversão por cidade
  conversaoPorCidade: [
    { cidade: 'São Paulo', txConversao: 1.51, change: 36.6, trend: 'up' as const, receitaTotal: 29965.15, changeReceita: 4.6, trendReceita: 'up' as const },
    { cidade: 'Rio de Janeiro', txConversao: 0.69, change: -39.1, trend: 'down' as const, receitaTotal: 4213.62, changeReceita: -61.6, trendReceita: 'down' as const },
    { cidade: 'Curitiba', txConversao: 0.99, change: -17.5, trend: 'down' as const, receitaTotal: 4018.86, changeReceita: -42.5, trendReceita: 'down' as const },
    { cidade: 'Belo Horizonte', txConversao: 0.92, change: -13.6, trend: 'down' as const, receitaTotal: 3514.26, changeReceita: -7.9, trendReceita: 'down' as const },
    { cidade: 'Campinas', txConversao: 1.01, change: -18.9, trend: 'down' as const, receitaTotal: 3110.38, changeReceita: -28.5, trendReceita: 'down' as const }
  ],

  // Dados para conversão por dispositivos
  conversaoPorDispositivos: [
    { dispositivo: 'mobile', txConversao: 0.73, change: -13.3, trend: 'down' as const, receitaTotal: 80102.83, changeReceita: -26.0, trendReceita: 'down' as const, percentual: 65 },
    { dispositivo: 'desktop', txConversao: 2.29, change: -21.5, trend: 'down' as const, receitaTotal: 34453.57, changeReceita: -30.5, trendReceita: 'down' as const, percentual: 28 },
    { dispositivo: 'tablet', txConversao: 0.74, change: 121.5, trend: 'up' as const, receitaTotal: 181.05, changeReceita: -25.5, trendReceita: 'down' as const, percentual: 7 }
  ],

  // Dados para conversão por grupo de canais
  conversaoPorGrupoCanais: [
    { grupo: 'Paid Social', txConversao: 0.43, change: -10.8, trend: 'down' as const, receitaTotal: 28769.49, changeReceita: -14.6, trendReceita: 'down' as const },
    { grupo: 'Paid Search', txConversao: 2.49, change: -12.4, trend: 'down' as const, receitaTotal: 23411.15, changeReceita: -33.1, trendReceita: 'down' as const },
    { grupo: 'Cross-network', txConversao: 2.14, change: -18.3, trend: 'down' as const, receitaTotal: 18955.7, changeReceita: -26.0, trendReceita: 'down' as const },
    { grupo: 'Referral', txConversao: 1.29, change: -21.6, trend: 'down' as const, receitaTotal: 17521.49, changeReceita: -32.2, trendReceita: 'down' as const },
    { grupo: 'Direct', txConversao: 0.73, change: -25.7, trend: 'down' as const, receitaTotal: 10430.38, changeReceita: -51.4, trendReceita: 'down' as const }
  ],

  // Dados para conversão por idade
  conversaoPorIdade: [
    { idade: 'unknown', txConversao: 0.75, change: -10.7, trend: 'down' as const, receitaTotal: 79900.01, changeReceita: -24.5, trendReceita: 'down' as const },
    { idade: '18-24', txConversao: 1.97, change: -28.0, trend: 'down' as const, receitaTotal: 24265.53, changeReceita: -37.0, trendReceita: 'down' as const },
    { idade: '25-34', txConversao: 1.75, change: 17.3, trend: 'up' as const, receitaTotal: 8263.09, changeReceita: 5.5, trendReceita: 'up' as const },
    { idade: '45-54', txConversao: 1.88, change: 13.1, trend: 'up' as const, receitaTotal: 1311.5, changeReceita: -32.7, trendReceita: 'down' as const },
    { idade: '35-44', txConversao: 0.54, change: -59.3, trend: 'down' as const, receitaTotal: 893.51, changeReceita: -62.0, trendReceita: 'down' as const }
  ],

  // Dados para conversão por gênero
  conversaoPorGenero: [
    { genero: 'unknown', txConversao: 0.73, change: -12.1, trend: 'down' as const, receitaTotal: 76735.73, changeReceita: -26.1, trendReceita: 'down' as const },
    { genero: 'male', txConversao: 1.82, change: -14.1, trend: 'down' as const, receitaTotal: 28391.21, changeReceita: -20.2, trendReceita: 'down' as const },
    { genero: 'female', txConversao: 1.57, change: -36.9, trend: 'down' as const, receitaTotal: 9610.51, changeReceita: -48.4, trendReceita: 'down' as const }
  ]
}; */

// Componente padronizado para Produtos Mais Vendidos
const ProdutosMaisVendidosTable = ({
  data,
  total,
  pagination,
  onRowClick,
  isRowSelected,
  clickableRows,
  loading,
}: {
  data: TableRow[];
  /**
   * <M-227> O "Total Geral" que a rota publica em `total_row`.
   *
   * Sem ele, `StandardizedTable` desenha o rodapé sozinha, somando coluna por coluna as dez
   * linhas da **página** — inclusive as duas colunas de taxa, que não declaram `type` e por
   * isso são inferidas como número. É o que dava TX Carrinho 35,61% e TX Conv. Itens 303,41%
   * (somas de razões) ao lado de 3.267 vistos e 96 carrinhos (somas de uma página só).
   */
  total?: TableRow;
  pagination?: {
    current: number;
    pageSize: number;
    total: number;
    onPageChange?: (page: number) => void;
  };
  /** A busca que alimenta a tabela está em voo. */
  loading?: boolean;
} & DrilldownTableProps) => {
  const columns: TableColumn[] = [
    {
      key: "item_name",
      header: "Nome do Item",
      align: "left",
      render: (value, _item, index, isTotal) =>
        isTotal ? value : `${index + 1}. ${value}`,
    },
    {
      key: "items_viewed",
      header: "Itens Vistos",
      align: "right",
      render: (value) => {
        const viewed = Number(value || 0);
        return viewed > 0 ? (
          formatInteger(viewed)
        ) : (
          <span
            className="cursor-help"
            title="Métrica não disponível quando agregado por produto no GA4"
          >
            N/D
          </span>
        );
      },
    },
    {
      key: "items_viewed_change_percentage",
      header: "% Δ",
      align: "right",
      render: (value) => <TrendIndicator value={Number(value || 0)} />,
    },
    {
      key: "sessions",
      header: "Sessões",
      align: "right",
      visible: false,
      render: (value) => formatInteger(Number(value || 0)),
    },
    {
      key: "sessions_change_percentage",
      header: "% Δ",
      align: "right",
      visible: false,
      render: (value) => <TrendIndicator value={Number(value || 0)} />,
    },
    {
      key: "session_conversion_rate",
      header: "TX Conv. Da Sessão",
      align: "right",
      visible: false,
      render: (value) => formatPercentage(Number(value || 0)),
    },
    {
      key: "session_conversion_rate_change_percentage",
      header: "% Δ",
      align: "right",
      visible: false,
      render: (value) => <TrendIndicator value={Number(value || 0)} />,
    },
    {
      key: "items_added_to_cart",
      header: "Carrinhos",
      align: "right",
      render: (value) => formatInteger(Number(value || 0)),
    },
    {
      key: "items_added_to_cart_change_percentage",
      header: "% Δ",
      align: "right",
      render: (value) => <TrendIndicator value={Number(value || 0)} />,
    },
    {
      key: "tx_carrinho",
      header: "TX Carrinho",
      align: "right",
      render: (value) => formatPercentage(Number(value || 0)),
    },
    {
      key: "tx_carrinho_change_percentage",
      header: "% Δ",
      align: "right",
      render: (value) => <TrendIndicator value={Number(value || 0)} />,
    },
    {
      key: "items_purchased",
      header: "Itens Comprados",
      align: "right",
      render: (value) => formatInteger(Number(value || 0)),
    },
    {
      key: "items_purchased_change_percentage",
      header: "% Δ",
      align: "right",
      render: (value) => <TrendIndicator value={Number(value || 0)} />,
    },
    {
      key: "tx_conv_itens",
      header: "TX Conv. Itens",
      align: "right",
      render: (value) => formatPercentage(Number(value || 0)),
    },
    {
      key: "tx_conv_itens_change_percentage",
      header: "% Δ",
      align: "right",
      render: (value) => <TrendIndicator value={Number(value || 0)} />,
    },
    {
      key: "purchase_revenue",
      header: "Receita do Item",
      align: "right",
      render: (value) => formatCurrency(Number(value || 0)),
    },
    {
      key: "purchase_revenue_change_percentage",
      header: "% Δ",
      align: "right",
      render: (value) => <TrendIndicator value={Number(value || 0)} />,
    },
  ];

  return (
    <StandardizedTable
      title="PRODUTOS MAIS VENDIDOS"
      description="Produtos com melhor performance de vendas"
      columns={columns}
      data={data}
      total={total}
      pagination={pagination}
      onRowClick={onRowClick}
      selectedRowIndex={isRowSelected}
      clickableRows={clickableRows}
      loading={loading}
    />
  );
};

// Componente padronizado para Conversão por Origem/Mídia
const ConversaoPorOrigemMidiaTable = ({
  data,
  total,
  pagination,
  onRowClick,
  isRowSelected,
  clickableRows,
  loading,
}: {
  data: TableRow[];
  /**
   * <M-228> O "Total Geral" que a rota publica em `sources.total_row`.
   *
   * Sem ele, `StandardizedTable` desenha o rodapé sozinha, somando coluna por coluna as dez
   * linhas da **página** — e a tabela é paginada pelo servidor, então essas dez são uma
   * fatia de um conjunto maior. `TX Conversão` piora o quadro por ser somada em vez de
   * promediada: a coluna não declara `type`, `inferColumnType` a classifica como número
   * pelos valores, e o rodapé soma razões. Medido na conta auditada: 26,01% de TX e
   * R$ 29.591,96 de receita contra 0,15% e R$ 32.077,06 do conjunto de 53 origens.
   */
  total?: TableRow;
  pagination?: {
    current: number;
    pageSize: number;
    total: number;
    onPageChange?: (page: number) => void;
  };
  /** A busca que alimenta a tabela está em voo. */
  loading?: boolean;
} & DrilldownTableProps) => {
  const columns: TableColumn[] = [
    {
      key: "source_medium",
      header: "Origem / mídia da sessão",
      align: "left",
      render: (value, item, index, isTotal) =>
        isTotal ? value : `${index + 1}. ${value}`,
    },
    {
      key: "conversion_rate",
      header: "TX Conversão",
      align: "right",
      render: (value) => formatPercentage(Number(value || 0)),
    },
    {
      key: "conversion_rate_change_percentage",
      header: "% Δ",
      align: "right",
      render: (value) => <TrendIndicator value={Number(value || 0)} />,
    },
    {
      key: "total_revenue",
      header: "Receita total",
      align: "right",
      render: (value) => formatCurrency(Number(value || 0)),
    },
    {
      key: "total_revenue_change_percentage",
      header: "% Δ",
      align: "right",
      render: (value) => <TrendIndicator value={Number(value || 0)} />,
    },
    {
      key: "sessions",
      header: "Sessões",
      align: "right",
      visible: false,
      render: (value) => formatInteger(Number(value || 0)),
    },
    {
      key: "sessions_change_percentage",
      header: "% Δ",
      align: "right",
      visible: false,
      render: (value) => <TrendIndicator value={Number(value || 0)} />,
    },
    {
      key: "engaged_sessions",
      header: "Sessões engajadas",
      align: "right",
      visible: false,
      render: (value) => {
        const v = Number(value || 0);
        return v > 0 ? formatInteger(v) : "N/D";
      },
    },
    {
      key: "engaged_sessions_change_percentage",
      header: "% Δ",
      align: "right",
      visible: false,
      render: (value) => <TrendIndicator value={Number(value || 0)} />,
    },
    {
      key: "total_users",
      header: "Total de usuários",
      align: "right",
      visible: false,
      render: (value) => formatInteger(Number(value || 0)),
    },
    {
      key: "total_users_change_percentage",
      header: "% Δ",
      align: "right",
      visible: false,
      render: (value) => <TrendIndicator value={Number(value || 0)} />,
    },
    {
      key: "orders",
      header: "Pedidos",
      align: "right",
      visible: false,
      render: (value) => formatInteger(Number(value || 0)),
    },
    {
      key: "orders_change_percentage",
      header: "% Δ",
      align: "right",
      visible: false,
      render: (value) => <TrendIndicator value={Number(value || 0)} />,
    },
    {
      key: "ticket_medio",
      header: "TM",
      align: "right",
      visible: false,
      render: (value) => formatCurrency(Number(value || 0)),
    },
    {
      key: "ticket_medio_change_percentage",
      header: "% Δ",
      align: "right",
      visible: false,
      render: (value) => <TrendIndicator value={Number(value || 0)} />,
    },
  ];

  return (
    <StandardizedTable
      title="CONVERSÃO POR ORIGEM/MÍDIA"
      description="Análise de conversão por origem e mídia da sessão"
      columns={columns}
      data={data}
      total={total}
      pagination={pagination}
      onRowClick={onRowClick}
      selectedRowIndex={isRowSelected}
      clickableRows={clickableRows}
      loading={loading}
    />
  );
};

// Componente padronizado para Conversão por Campanha (UTM)
const ConversaoPorCampanhaTable = ({
  data,
  total,
  pagination,
  onRowClick,
  isRowSelected,
  clickableRows,
  loading,
}: {
  data: TableRow[];
  /**
   * <M-229> O "Total Geral" que `GET /api/v1/conversion-rate/utm-campaigns` publica em
   * `total_row`, agregado sobre o conjunto inteiro antes de paginar.
   *
   * Sem ele, `StandardizedTable` desenha o rodapé sozinha, somando coluna por coluna as dez
   * linhas da **página** — e a tabela é paginada pelo servidor, então essas dez são uma fatia
   * de um conjunto maior. `TX Conversão` piora o quadro por ser somada em vez de promediada,
   * pela mesma razão de M-228: a coluna não declara `type`, `inferColumnType` a classifica
   * como número pelos valores e o rodapé soma razões. Medido na conta auditada: 25,62% de TX
   * e R$ 30.809,01 de receita contra 0,15% e R$ 32.077,06 das 68 campanhas do conjunto.
   */
  total?: TableRow;
  pagination?: {
    current: number;
    pageSize: number;
    total: number;
    onPageChange?: (page: number) => void;
  };
  /** A busca que alimenta a tabela está em voo. */
  loading?: boolean;
} & DrilldownTableProps) => {
  const columns: TableColumn[] = [
    {
      key: "campaign",
      header: "Campanha da sessão",
      align: "left",
      render: (value, item, index, isTotal) =>
        isTotal ? value : `${index + 1}. ${value}`,
    },
    {
      key: "conversion_rate",
      header: "TX Conversão",
      align: "right",
      render: (value) => formatPercentage(Number(value || 0)),
    },
    {
      key: "conversion_rate_change_percentage",
      header: "% Δ",
      align: "right",
      render: (value) => <TrendIndicator value={Number(value || 0)} />,
    },
    {
      key: "total_revenue",
      header: "Receita total",
      align: "right",
      render: (value) => formatCurrency(Number(value || 0)),
    },
    {
      key: "total_revenue_change_percentage",
      header: "% Δ",
      align: "right",
      render: (value) => <TrendIndicator value={Number(value || 0)} />,
    },
    {
      key: "sessions",
      header: "Sessões",
      align: "right",
      visible: false,
      render: (value) => formatInteger(Number(value || 0)),
    },
    {
      key: "sessions_change_percentage",
      header: "% Δ",
      align: "right",
      visible: false,
      render: (value) => <TrendIndicator value={Number(value || 0)} />,
    },
    {
      key: "engaged_sessions",
      header: "Sessões engajadas",
      align: "right",
      visible: false,
      render: (value) => {
        const v = Number(value || 0);
        return v > 0 ? formatInteger(v) : "N/D";
      },
    },
    {
      key: "engaged_sessions_change_percentage",
      header: "% Δ",
      align: "right",
      visible: false,
      render: (value) => <TrendIndicator value={Number(value || 0)} />,
    },
    {
      key: "total_users",
      header: "Total de usuários",
      align: "right",
      visible: false,
      render: (value) => formatInteger(Number(value || 0)),
    },
    {
      key: "total_users_change_percentage",
      header: "% Δ",
      align: "right",
      visible: false,
      render: (value) => <TrendIndicator value={Number(value || 0)} />,
    },
    {
      key: "orders",
      header: "Pedidos",
      align: "right",
      visible: false,
      render: (value) => formatInteger(Number(value || 0)),
    },
    {
      key: "orders_change_percentage",
      header: "% Δ",
      align: "right",
      visible: false,
      render: (value) => <TrendIndicator value={Number(value || 0)} />,
    },
    {
      key: "ticket_medio",
      header: "TM",
      align: "right",
      visible: false,
      render: (value) => formatCurrency(Number(value || 0)),
    },
    {
      key: "ticket_medio_change_percentage",
      header: "% Δ",
      align: "right",
      visible: false,
      render: (value) => <TrendIndicator value={Number(value || 0)} />,
    },
  ];

  return (
    <StandardizedTable
      title="CONVERSÃO POR CAMPANHA (UTM)"
      description="Análise de conversão por campanhas UTM"
      columns={columns}
      data={data}
      total={total}
      pagination={pagination}
      onRowClick={onRowClick}
      selectedRowIndex={isRowSelected}
      clickableRows={clickableRows}
      loading={loading}
    />
  );
};

// Componente padronizado para Conversão por Estado
const ConversaoPorEstadoTable = ({
  data,
  total,
  pagination,
  onRowClick,
  isRowSelected,
  clickableRows,
  loading,
}: {
  data: TableRow[];
  /**
   * <M-230> O "Total Geral" que a rota publica em `total_row`, agregado sobre todo o conjunto antes de paginar.
   *
   * Sem ele, `StandardizedTable` desenha o rodapé sozinha e soma coluna por coluna as linhas
   * que tem em mãos — `TX Conversão` inclusive, que não declara `type` e por isso é
   * classificada como número por `inferColumnType`. Medido na conta auditada: as dez linhas da página 1 somam 1,60% de TX, contra os 0,15% dos 89 estados do conjunto.
   */
  total?: TableRow;
  pagination?: {
    current: number;
    pageSize: number;
    total: number;
    onPageChange?: (page: number) => void;
  };
  /** A busca que alimenta a tabela está em voo. */
  loading?: boolean;
} & DrilldownTableProps) => {
  const columns: TableColumn[] = [
    {
      key: "estado",
      header: "Região",
      align: "left",
      render: (value, item, index, isTotal) =>
        isTotal ? value : `${index + 1}. ${value}`,
    },
    {
      key: "txConversao",
      header: "TX Conversão",
      align: "right",
      render: (value) => formatPercentage(value),
    },
    {
      key: "change",
      header: "% Δ",
      align: "right",
      render: (value) => <TrendIndicator value={value} />,
    },
    {
      key: "receitaTotal",
      header: "Receita total",
      align: "right",
      render: (value) => formatCurrency(value),
    },
    {
      key: "changeReceita",
      header: "% Δ",
      align: "right",
      render: (value) => <TrendIndicator value={value} />,
    },
    {
      key: "sessions",
      header: "Sessões",
      align: "right",
      visible: false,
      render: (value) => formatInteger(Number(value || 0)),
    },
    {
      key: "sessions_change_percentage",
      header: "% Δ",
      align: "right",
      visible: false,
      render: (value) => <TrendIndicator value={Number(value || 0)} />,
    },
    {
      key: "engaged_sessions",
      header: "Sessões engajadas",
      align: "right",
      visible: false,
      render: (value) => {
        const v = Number(value || 0);
        return v > 0 ? formatInteger(v) : "N/D";
      },
    },
    {
      key: "engaged_sessions_change_percentage",
      header: "% Δ",
      align: "right",
      visible: false,
      render: (value) => <TrendIndicator value={Number(value || 0)} />,
    },
    {
      key: "total_users",
      header: "Total de usuários",
      align: "right",
      visible: false,
      render: (value) => formatInteger(Number(value || 0)),
    },
    {
      key: "total_users_change_percentage",
      header: "% Δ",
      align: "right",
      visible: false,
      render: (value) => <TrendIndicator value={Number(value || 0)} />,
    },
    {
      key: "orders",
      header: "Pedidos",
      align: "right",
      visible: false,
      render: (value) => formatInteger(Number(value || 0)),
    },
    {
      key: "orders_change_percentage",
      header: "% Δ",
      align: "right",
      visible: false,
      render: (value) => <TrendIndicator value={Number(value || 0)} />,
    },
    {
      key: "ticket_medio",
      header: "TM",
      align: "right",
      visible: false,
      render: (value) => formatCurrency(Number(value || 0)),
    },
    {
      key: "ticket_medio_change_percentage",
      header: "% Δ",
      align: "right",
      visible: false,
      render: (value) => <TrendIndicator value={Number(value || 0)} />,
    },
  ];

  return (
    <StandardizedTable
      title="CONVERSÃO POR ESTADO"
      description="Análise de conversão por estado"
      columns={columns}
      data={data}
      total={total}
      pagination={pagination}
      onRowClick={onRowClick}
      selectedRowIndex={isRowSelected}
      clickableRows={clickableRows}
      loading={loading}
    />
  );
};

// Componente padronizado para Conversão por Cidade
const ConversaoPorCidadeTable = ({
  data,
  total,
  pagination,
  onRowClick,
  isRowSelected,
  clickableRows,
  loading,
}: {
  data: TableRow[];
  /**
   * <M-230> O "Total Geral" que a rota publica em `total_row`, agregado sobre todo o conjunto antes de paginar.
   *
   * Sem ele, `StandardizedTable` desenha o rodapé sozinha e soma coluna por coluna as linhas
   * que tem em mãos — `TX Conversão` inclusive, que não declara `type` e por isso é
   * classificada como número por `inferColumnType`. Medido na conta auditada: as dez linhas da página 1 somam 15,35% de TX, contra os 0,14% das 1.220 cidades do conjunto.
   */
  total?: TableRow;
  pagination?: {
    current: number;
    pageSize: number;
    total: number;
    onPageChange?: (page: number) => void;
  };
  /** A busca que alimenta a tabela está em voo. */
  loading?: boolean;
} & DrilldownTableProps) => {
  const columns: TableColumn[] = [
    {
      key: "cidade",
      header: "Cidade",
      align: "left",
      render: (value, item, index, isTotal) =>
        isTotal ? value : `${index + 1}. ${value}`,
    },
    {
      key: "txConversao",
      header: "TX Conversão",
      align: "right",
      render: (value) => formatPercentage(value),
    },
    {
      key: "change",
      header: "% Δ",
      align: "right",
      render: (value) => <TrendIndicator value={value} />,
    },
    {
      key: "receitaTotal",
      header: "Receita total",
      align: "right",
      render: (value) => formatCurrency(value),
    },
    {
      key: "changeReceita",
      header: "% Δ",
      align: "right",
      render: (value) => <TrendIndicator value={value} />,
    },
    {
      key: "sessions",
      header: "Sessões",
      align: "right",
      visible: false,
      render: (value) => formatInteger(Number(value || 0)),
    },
    {
      key: "sessions_change_percentage",
      header: "% Δ",
      align: "right",
      visible: false,
      render: (value) => <TrendIndicator value={Number(value || 0)} />,
    },
    {
      key: "engaged_sessions",
      header: "Sessões engajadas",
      align: "right",
      visible: false,
      render: (value) => {
        const v = Number(value || 0);
        return v > 0 ? formatInteger(v) : "N/D";
      },
    },
    {
      key: "engaged_sessions_change_percentage",
      header: "% Δ",
      align: "right",
      visible: false,
      render: (value) => <TrendIndicator value={Number(value || 0)} />,
    },
    {
      key: "total_users",
      header: "Total de usuários",
      align: "right",
      visible: false,
      render: (value) => formatInteger(Number(value || 0)),
    },
    {
      key: "total_users_change_percentage",
      header: "% Δ",
      align: "right",
      visible: false,
      render: (value) => <TrendIndicator value={Number(value || 0)} />,
    },
    {
      key: "orders",
      header: "Pedidos",
      align: "right",
      visible: false,
      render: (value) => formatInteger(Number(value || 0)),
    },
    {
      key: "orders_change_percentage",
      header: "% Δ",
      align: "right",
      visible: false,
      render: (value) => <TrendIndicator value={Number(value || 0)} />,
    },
    {
      key: "ticket_medio",
      header: "TM",
      align: "right",
      visible: false,
      render: (value) => formatCurrency(Number(value || 0)),
    },
    {
      key: "ticket_medio_change_percentage",
      header: "% Δ",
      align: "right",
      visible: false,
      render: (value) => <TrendIndicator value={Number(value || 0)} />,
    },
  ];

  return (
    <StandardizedTable
      title="CONVERSÃO POR CIDADE"
      description="Análise de conversão por cidade"
      columns={columns}
      data={data}
      total={total}
      pagination={pagination}
      onRowClick={onRowClick}
      selectedRowIndex={isRowSelected}
      clickableRows={clickableRows}
      loading={loading}
    />
  );
};

// Componente padronizado para Conversão por Dispositivos
const ConversaoPorDispositivosTable = ({
  data,
  total,
  onRowClick,
  isRowSelected,
  clickableRows,
  loading,
}: {
  data: TableRow[];
  /**
   * <M-230> O "Total Geral" que a rota publica em `total_row`, somado sobre os dispositivos.
   *
   * Sem ele, `StandardizedTable` desenha o rodapé sozinha e soma coluna por coluna as linhas
   * que tem em mãos — `TX Conversão` inclusive, que não declara `type` e por isso é
   * classificada como número por `inferColumnType`. `GET /api/v1/devices-sales` não pagina,
   * então aqui o defeito é só o padrão A: as três linhas somam 0,80% de TX, contra os 0,15%
   * da razão dos totais.
   */
  total?: TableRow;
  /** A busca que alimenta a tabela está em voo. */
  loading?: boolean;
} & DrilldownTableProps) => {
  const columns: TableColumn[] = [
    {
      key: "dispositivo",
      header: "Categoria de dispositivo",
      align: "left",
      render: (value, item, index, isTotal) =>
        isTotal ? value : `${index + 1}. ${value}`,
    },
    {
      key: "txConversao",
      header: "TX Conversão",
      align: "right",
      render: (value) => formatPercentage(value),
    },
    {
      key: "change",
      header: "% Δ",
      align: "right",
      render: (value) => <TrendIndicator value={value} />,
    },
    {
      key: "receitaTotal",
      header: "Receita total",
      align: "right",
      render: (value) => formatCurrency(value),
    },
    {
      key: "changeReceita",
      header: "% Δ",
      align: "right",
      render: (value) => <TrendIndicator value={value} />,
    },
    {
      key: "sessions",
      header: "Sessões",
      align: "right",
      visible: false,
      render: (value) => formatInteger(Number(value || 0)),
    },
    {
      key: "sessions_change_percentage",
      header: "% Δ",
      align: "right",
      visible: false,
      render: (value) => <TrendIndicator value={Number(value || 0)} />,
    },
    {
      key: "engaged_sessions",
      header: "Sessões engajadas",
      align: "right",
      visible: false,
      render: (value) => {
        const v = Number(value || 0);
        return v > 0 ? formatInteger(v) : "N/D";
      },
    },
    {
      key: "engaged_sessions_change_percentage",
      header: "% Δ",
      align: "right",
      visible: false,
      render: (value) => <TrendIndicator value={Number(value || 0)} />,
    },
    {
      key: "total_users",
      header: "Total de usuários",
      align: "right",
      visible: false,
      render: (value) => formatInteger(Number(value || 0)),
    },
    {
      key: "total_users_change_percentage",
      header: "% Δ",
      align: "right",
      visible: false,
      render: (value) => <TrendIndicator value={Number(value || 0)} />,
    },
    {
      key: "orders",
      header: "Pedidos",
      align: "right",
      visible: false,
      render: (value) => formatInteger(Number(value || 0)),
    },
    {
      key: "orders_change_percentage",
      header: "% Δ",
      align: "right",
      visible: false,
      render: (value) => <TrendIndicator value={Number(value || 0)} />,
    },
    {
      key: "ticket_medio",
      header: "TM",
      align: "right",
      visible: false,
      render: (value) => formatCurrency(Number(value || 0)),
    },
    {
      key: "ticket_medio_change_percentage",
      header: "% Δ",
      align: "right",
      visible: false,
      render: (value) => <TrendIndicator value={Number(value || 0)} />,
    },
  ];

  return (
    <StandardizedTable
      title="CONVERSÃO POR DISPOSITIVOS"
      description="Análise de conversão por categoria de dispositivo"
      columns={columns}
      data={data}
      total={total}
      onRowClick={onRowClick}
      selectedRowIndex={isRowSelected}
      clickableRows={clickableRows}
      loading={loading}
    />
  );
};

// Componente padronizado para Conversão por Grupo de Canais
const ConversaoPorGrupoCanaisTable = ({
  data,
  total,
  pagination,
  onRowClick,
  isRowSelected,
  clickableRows,
  loading,
}: {
  data: TableRow[];
  /**
   * <M-230> O "Total Geral" que a rota publica em `total_row`, agregado sobre todo o conjunto antes de paginar.
   *
   * Sem ele, `StandardizedTable` desenha o rodapé sozinha e soma coluna por coluna as linhas
   * que tem em mãos — `TX Conversão` inclusive, que não declara `type` e por isso é
   * classificada como número por `inferColumnType`. Medido na conta auditada: as dez linhas da página 1 somam 3,72% de TX, contra os 0,15% dos 11 canais do conjunto.
   */
  total?: TableRow;
  pagination?: {
    current: number;
    pageSize: number;
    total: number;
    onPageChange?: (page: number) => void;
  };
  /** A busca que alimenta a tabela está em voo. */
  loading?: boolean;
} & DrilldownTableProps) => {
  const columns: TableColumn[] = [
    {
      key: "grupo",
      header: "Grupo De Canais",
      align: "left",
      render: (value, item, index, isTotal) =>
        isTotal ? value : `${index + 1}. ${value}`,
    },
    {
      key: "txConversao",
      header: "TX Conversão",
      align: "right",
      render: (value) => formatPercentage(value),
    },
    {
      key: "change",
      header: "% Δ",
      align: "right",
      render: (value) => <TrendIndicator value={value} />,
    },
    {
      key: "receitaTotal",
      header: "Receita total",
      align: "right",
      render: (value) => formatCurrency(value),
    },
    {
      key: "changeReceita",
      header: "% Δ",
      align: "right",
      render: (value) => <TrendIndicator value={value} />,
    },
    {
      key: "sessions",
      header: "Sessões",
      align: "right",
      visible: false,
      render: (value) => formatInteger(Number(value || 0)),
    },
    {
      key: "sessions_change_percentage",
      header: "% Δ",
      align: "right",
      visible: false,
      render: (value) => <TrendIndicator value={Number(value || 0)} />,
    },
    {
      key: "engaged_sessions",
      header: "Sessões engajadas",
      align: "right",
      visible: false,
      render: (value) => {
        const v = Number(value || 0);
        return v > 0 ? formatInteger(v) : "N/D";
      },
    },
    {
      key: "engaged_sessions_change_percentage",
      header: "% Δ",
      align: "right",
      visible: false,
      render: (value) => <TrendIndicator value={Number(value || 0)} />,
    },
    {
      key: "total_users",
      header: "Total de usuários",
      align: "right",
      visible: false,
      render: (value) => formatInteger(Number(value || 0)),
    },
    {
      key: "total_users_change_percentage",
      header: "% Δ",
      align: "right",
      visible: false,
      render: (value) => <TrendIndicator value={Number(value || 0)} />,
    },
    {
      key: "orders",
      header: "Pedidos",
      align: "right",
      visible: false,
      render: (value) => formatInteger(Number(value || 0)),
    },
    {
      key: "orders_change_percentage",
      header: "% Δ",
      align: "right",
      visible: false,
      render: (value) => <TrendIndicator value={Number(value || 0)} />,
    },
    {
      key: "ticket_medio",
      header: "TM",
      align: "right",
      visible: false,
      render: (value) => formatCurrency(Number(value || 0)),
    },
    {
      key: "ticket_medio_change_percentage",
      header: "% Δ",
      align: "right",
      visible: false,
      render: (value) => <TrendIndicator value={Number(value || 0)} />,
    },
  ];

  return (
    <StandardizedTable
      title="CONVERSÃO POR GRUPO CANAIS PADRÃO SESSÃO"
      description="Análise de conversão por grupo de canais padrão da sessão"
      columns={columns}
      data={data}
      total={total}
      pagination={pagination}
      onRowClick={onRowClick}
      selectedRowIndex={isRowSelected}
      clickableRows={clickableRows}
      loading={loading}
    />
  );
};

// Componente padronizado para Conversão por Idade
const ConversaoPorIdadeTable = ({
  data,
  total,
  pagination,
  onRowClick,
  isRowSelected,
  clickableRows,
  loading,
}: {
  data: TableRow[];
  /**
   * <M-230> O "Total Geral" que a rota publica em `total_row`, agregado sobre o período inteiro, e não sobre a soma das faixas (M-226).
   *
   * Sem ele, `StandardizedTable` desenha o rodapé sozinha e soma coluna por coluna as linhas
   * que tem em mãos — `TX Conversão` inclusive, que não declara `type` e por isso é
   * classificada como número por `inferColumnType`. Medido na conta auditada: as sete faixas somam 1,14% de TX e R$ 4.121,38 de TM, contra 0,15% e R$ 866,95 do período.
   */
  total?: TableRow;
  pagination?: {
    current: number;
    pageSize: number;
    total: number;
    onPageChange?: (page: number) => void;
  };
  /** A busca que alimenta a tabela está em voo. */
  loading?: boolean;
} & DrilldownTableProps) => {
  const columns: TableColumn[] = [
    {
      key: "idade",
      header: "Idade",
      align: "left",
      render: (value, item, index, isTotal) =>
        isTotal ? value : `${index + 1}. ${value}`,
    },
    {
      key: "txConversao",
      header: "TX Conversão",
      align: "right",
      render: (value) => formatPercentage(value),
    },
    {
      key: "change",
      header: "% Δ",
      align: "right",
      render: (value) => <TrendIndicator value={value} />,
    },
    {
      key: "receitaTotal",
      header: "Receita total",
      align: "right",
      render: (value) => formatCurrency(value),
    },
    {
      key: "changeReceita",
      header: "% Δ",
      align: "right",
      render: (value) => <TrendIndicator value={value} />,
    },
    {
      key: "sessions",
      header: "Sessões",
      align: "right",
      visible: false,
      render: (value) => formatInteger(Number(value || 0)),
    },
    {
      key: "sessions_change_percentage",
      header: "% Δ",
      align: "right",
      visible: false,
      render: (value) => <TrendIndicator value={Number(value || 0)} />,
    },
    {
      key: "engaged_sessions",
      header: "Sessões engajadas",
      align: "right",
      visible: false,
      render: (value) => {
        const v = Number(value || 0);
        return v > 0 ? formatInteger(v) : "N/D";
      },
    },
    {
      key: "engaged_sessions_change_percentage",
      header: "% Δ",
      align: "right",
      visible: false,
      render: (value) => <TrendIndicator value={Number(value || 0)} />,
    },
    {
      key: "total_users",
      header: "Total de usuários",
      align: "right",
      visible: false,
      render: (value) => formatInteger(Number(value || 0)),
    },
    {
      key: "total_users_change_percentage",
      header: "% Δ",
      align: "right",
      visible: false,
      render: (value) => <TrendIndicator value={Number(value || 0)} />,
    },
    {
      key: "orders",
      header: "Pedidos",
      align: "right",
      visible: false,
      render: (value) => formatInteger(Number(value || 0)),
    },
    {
      key: "orders_change_percentage",
      header: "% Δ",
      align: "right",
      visible: false,
      render: (value) => <TrendIndicator value={Number(value || 0)} />,
    },
    {
      key: "ticket_medio",
      header: "TM",
      align: "right",
      visible: false,
      render: (value) => formatCurrency(Number(value || 0)),
    },
    {
      key: "ticket_medio_change_percentage",
      header: "% Δ",
      align: "right",
      visible: false,
      render: (value) => <TrendIndicator value={Number(value || 0)} />,
    },
  ];

  return (
    <StandardizedTable
      title="CONVERSÃO POR IDADE"
      description="Análise de conversão por faixa etária"
      columns={columns}
      data={data}
      total={total}
      pagination={pagination}
      onRowClick={onRowClick}
      selectedRowIndex={isRowSelected}
      clickableRows={clickableRows}
      loading={loading}
    />
  );
};

// Componente padronizado para Conversão por Gênero
const ConversaoPorGeneroTable = ({
  data,
  total,
  pagination,
  onRowClick,
  isRowSelected,
  clickableRows,
  loading,
}: {
  data: TableRow[];
  /**
   * <M-230> O "Total Geral" que a rota publica em `total_row`, agregado sobre o período inteiro, e não sobre a soma dos gêneros (M-226).
   *
   * Sem ele, `StandardizedTable` desenha o rodapé sozinha e soma coluna por coluna as linhas
   * que tem em mãos — `TX Conversão` inclusive, que não declara `type` e por isso é
   * classificada como número por `inferColumnType`. Medido na conta auditada: os três gêneros somam 0,49% de TX, contra os 0,15% do período.
   */
  total?: TableRow;
  pagination?: {
    current: number;
    pageSize: number;
    total: number;
    onPageChange?: (page: number) => void;
  };
  /** A busca que alimenta a tabela está em voo. */
  loading?: boolean;
} & DrilldownTableProps) => {
  const columns: TableColumn[] = [
    {
      key: "genero",
      header: "Gênero",
      align: "left",
      render: (value, item, index, isTotal) =>
        isTotal ? value : `${index + 1}. ${value}`,
    },
    {
      key: "txConversao",
      header: "TX Conversão",
      align: "right",
      render: (value) => formatPercentage(value),
    },
    {
      key: "change",
      header: "% Δ",
      align: "right",
      render: (value) => <TrendIndicator value={value} />,
    },
    {
      key: "receitaTotal",
      header: "Receita total",
      align: "right",
      render: (value) => formatCurrency(value),
    },
    {
      key: "changeReceita",
      header: "% Δ",
      align: "right",
      render: (value) => <TrendIndicator value={value} />,
    },
    {
      key: "sessions",
      header: "Sessões",
      align: "right",
      visible: false,
      render: (value) => formatInteger(Number(value || 0)),
    },
    {
      key: "sessions_change_percentage",
      header: "% Δ",
      align: "right",
      visible: false,
      render: (value) => <TrendIndicator value={Number(value || 0)} />,
    },
    {
      key: "engaged_sessions",
      header: "Sessões engajadas",
      align: "right",
      visible: false,
      render: (value) => {
        const v = Number(value || 0);
        return v > 0 ? formatInteger(v) : "N/D";
      },
    },
    {
      key: "engaged_sessions_change_percentage",
      header: "% Δ",
      align: "right",
      visible: false,
      render: (value) => <TrendIndicator value={Number(value || 0)} />,
    },
    {
      key: "total_users",
      header: "Total de usuários",
      align: "right",
      visible: false,
      render: (value) => formatInteger(Number(value || 0)),
    },
    {
      key: "total_users_change_percentage",
      header: "% Δ",
      align: "right",
      visible: false,
      render: (value) => <TrendIndicator value={Number(value || 0)} />,
    },
    {
      key: "orders",
      header: "Pedidos",
      align: "right",
      visible: false,
      render: (value) => formatInteger(Number(value || 0)),
    },
    {
      key: "orders_change_percentage",
      header: "% Δ",
      align: "right",
      visible: false,
      render: (value) => <TrendIndicator value={Number(value || 0)} />,
    },
    {
      key: "ticket_medio",
      header: "TM",
      align: "right",
      visible: false,
      render: (value) => formatCurrency(Number(value || 0)),
    },
    {
      key: "ticket_medio_change_percentage",
      header: "% Δ",
      align: "right",
      visible: false,
      render: (value) => <TrendIndicator value={Number(value || 0)} />,
    },
  ];

  return (
    <StandardizedTable
      title="CONVERSÃO POR GÊNERO"
      description="Análise de conversão por gênero"
      columns={columns}
      data={data}
      total={total}
      pagination={pagination}
      onRowClick={onRowClick}
      selectedRowIndex={isRowSelected}
      clickableRows={clickableRows}
      loading={loading}
    />
  );
};

// Funções de formatação
const formatCurrency = (value: number) => {
  return new Intl.NumberFormat("pt-BR", {
    style: "currency",
    currency: "BRL",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value);
};

const formatNumber = (value: number) => {
  return new Intl.NumberFormat("pt-BR").format(value);
};

const formatPercentage = (value: number) => {
  return `${Number(value ?? 0).toLocaleString("pt-BR", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}%`;
};

// Função de formatação baseada no tipo de dado (igual à Performance de Mídia)
const formatValueByType = (v: unknown, type: string) => {
  if (v === null || v === undefined) return "N/D";
  if (type === "CURRENCY")
    return new Intl.NumberFormat("pt-BR", {
      style: "currency",
      currency: "BRL",
    }).format(Number(v));
  if (type === "PERCENT") {
    return `${Number(v ?? 0).toLocaleString("pt-BR", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })}%`;
  }
  if (type === "NUMBER") return Number(v).toLocaleString("pt-BR");
  return String(v);
};

const getKpiFormat = (type: string) => {
  if (type === "CURRENCY") {
    return (n: number) =>
      new Intl.NumberFormat("pt-BR", {
        style: "currency",
        currency: "BRL",
      }).format(Number(n));
  }
  if (type === "PERCENT") {
    return (n: number) =>
      `${Number(n ?? 0).toLocaleString("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}%`;
  }
  return (n: number) => Number(n).toLocaleString("pt-BR");
};

/** Os oito cards saem do GA4 (`/google-analytics/conversion-rate/cards-kpis`). */
const FONTES_DOS_CARDS: readonly RequisitoDeFonte[] = ["ga4"];

// Nota: TrendIndicator removido - usando TrendIndicator padronizado de @/components/ui/standardized-table

/**
 * <M-209> As dimensões de drill desta página que **não** viram cross-filter de rota.
 *
 * `itemName` e `itemCategory` são de escopo de item, e a Data API recusa métrica de escopo de
 * sessão junto de dimensão de escopo de item — as dez rotas da página pedem `sessions`. Mandá-las
 * seria pedir um recorte que rota nenhuma tem como aplicar.
 *
 * <M-210> O clique que produzia `filter_product` **saiu** da página: a tabela PRODUTOS não é
 * mais clicável aqui, porque um chip de filtro ativo afirma um recorte que chegou, e este não
 * chegava a rota nenhuma.
 *
 * <M-212> E o de `filter_category` saiu junto: `CategoriasMaisVendidasTable` se liga sozinha
 * ao contexto de drill, e o `drilldownEnabled` dela — cujo padrão passou a ser `false` — deixa
 * de ligar o clique aqui. Com os dois fora, nenhuma fonte desta página produz qualquer uma das
 * duas chaves abaixo, e a peneira passou a ser uma barreira: ela existe para que uma tabela
 * compartilhada que volte a emitir dimensão de item não passe a mandá-la daqui. As outras seis
 * saíram da lista junto com as dimensões que as produziam.
 *
 * ⚠️ **Uma das dez rotas da página tem, sim, caminho para `filter_category`.**
 * `/acquisition/timeline` desvia para um recorte de item quando ele chega
 * (`AcquisitionKpisService.desvioDeEscopoDeItem`). Ela continua na peneira de propósito: um
 * chip que uma tabela em dez honra é pior do que um chip que nenhuma honra, porque a página
 * passaria a desenhar dois recortes ao mesmo tempo.
 */
const FILTROS_DE_ESCOPO_DE_ITEM = ["filter_product", "filter_category"];

const ConversionRatePageContent: React.FC = () => {
  const { filter: filterState } = useGlobalFilter();
  const comparisonEnabled = isComparisonEnabled(filterState);
  const { selectedAccount } = useAccount();
  const { campaignFiltersNonce, handleCampaignFiltersSaved } =
    useCampaignFiltersState();
  const drilldown = useDrilldownOptional();
  const customerUuid =
    selectedAccount?.customer_uuid ||
    localStorage.getItem("active_customer_uuid") ||
    null;
  // Filtro livre por item (mesmo padrão do SEO: um valor que altera toda a página)
  const [itemNameQuery, setItemNameQuery] = useState<string>("");
  const [itemNameOperator, setItemNameOperator] =
    useState<ItemNameOperator>("contains");
  const [itemNameApplied, setItemNameApplied] = useState<string | undefined>(
    undefined,
  );
  const [itemNameOperatorApplied, setItemNameOperatorApplied] =
    useState<ItemNameOperator>("contains");
  // Nasce ligado pelo mesmo motivo de `loadingKpis`: com `false`, o primeiro desenho mostrava
  // as tabelas dizendo "Nenhum dado encontrado" antes de a busca sair.
  const [loading, setLoading] = useState<boolean>(true);
  // Estados, cidades, dispositivos e grupo de canais vêm de um efeito próprio, com flag própria.
  const [loadingGeo, setLoadingGeo] = useState<boolean>(true);
  // Começa carregando: o primeiro desenho acontece antes de o efeito ligar a flag, e os oito
  // cards piscavam N/D antes de a busca sair.
  const [loadingKpis, setLoadingKpis] = useState<boolean>(true);
  const [erroKpis, setErroKpis] = useState<boolean>(false);
  const [kpis, setKpis] = useState<ConversionRateCardsKpisResponse | null>(
    null,
  );
  const [series, setSeries] = useState<TableRow[]>([]);
  const [barTimeline, setBarTimeline] = useState<TableRow[]>([]);
  const [products, setProducts] = useState<{
    items: TableRow[];
    pagination: TableRow;
    // <M-227> O "Total Geral" da tabela, calculado pela rota sobre o conjunto inteiro.
    totalRow: TableRow | null;
  } | null>(null);
  const [sources, setSources] = useState<{
    items: TableRow[];
    pagination: TableRow;
    // <M-228> O "Total Geral" da tabela, calculado pela rota sobre o conjunto inteiro.
    totalRow: TableRow | null;
  } | null>(null);
  const [campaigns, setCampaigns] = useState<{
    items: TableRow[];
    pagination: TableRow;
    // <M-229> O "Total Geral" da tabela, calculado pela rota sobre o conjunto inteiro.
    totalRow: TableRow | null;
  } | null>(null);
  const [statesData, setStatesData] = useState<RegionTableData | null>(null);
  const [citiesData, setCitiesData] = useState<RegionTableData | null>(null);
  const [devicesData, setDevicesData] = useState<DevCharts | null>(null);
  const [channelsData, setChannelsData] = useState<RegionTableData | null>(
    null,
  );
  const [demographics, setDemographics] = useState<DemographicsData | null>(
    null,
  );
  const [statesPage, setStatesPage] = useState<number>(1);
  const [citiesPage, setCitiesPage] = useState<number>(1);
  const [channelsPage, setChannelsPage] = useState<number>(1);
  const [agesPage, setAgesPage] = useState<number>(1);
  const [gendersPage, setGendersPage] = useState<number>(1);

  const [productsPage, setProductsPage] = useState<number>(1);
  const [sourcesPage, setSourcesPage] = useState<number>(1);
  const [campaignsPage, setCampaignsPage] = useState<number>(1);

  // Timeline do período anterior (para gráfico de TX Conversão)
  const [barTimelinePrev, setBarTimelinePrev] = useState<TableRow[]>([]);

  // ──── Cross-filtering: filtros da API (apiFilters) para cada request ────
  //
  // <M-209> Até aqui este objeto era montado, entrava nas dependências dos quatro effects e
  // **não era enviado em requisição nenhuma**: clicar numa linha redesenhava a página inteira
  // com exatamente os mesmos números, e os chips de `ActiveFiltersDisplay` diziam que havia um
  // recorte ativo. As dez rotas da página passaram a declarar os oito cross-filters de escopo
  // de sessão, e é isso que os manda.
  //
  // ⚠️ `filter_product` e `filter_category` ficam de fora, e não é esquecimento — ver
  // `FILTROS_DE_ESCOPO_DE_ITEM`.
  const drilldownExtraParams = useMemo(() => {
    const todos = drilldown?.apiFilters ?? {};
    return Object.fromEntries(
      Object.entries(todos).filter(
        ([nome]) => !FILTROS_DE_ESCOPO_DE_ITEM.includes(nome),
      ),
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [drilldown?.apiFiltersKey]);

  /**
   * O recorte **sem** a dimensão que a própria tabela desenha.
   *
   * Filtrar a tabela de estados por estado a reduz a uma linha, e ela deixa de ser tabela. As
   * duas rotas que servem dois blocos numa requisição — `/conversion-rate/full` e
   * `/conversion-rate/demographics-v2` — excluem do lado do servidor, porque aqui não daria.
   */
  const recorteExceto = useCallback(
    (dimensao: DrillDimension) => {
      const params = { ...drilldownExtraParams };
      delete params[`filter_${dimensao}`];
      return params;
    },
    [drilldownExtraParams],
  );

  // ──── Cross-filtering: row click handlers por dimensão ────
  const makeRowClick = useCallback(
    (dimension: DrillDimension, keyField: string) =>
      (row: TableRow, _index: number, event?: React.MouseEvent) => {
        if (!drilldown) return;
        const value = row[keyField] as string;
        if (!value || value === "N/D") return;
        const isMultiSelect = event?.ctrlKey || event?.metaKey;
        drilldown.selectRow(dimension, value, value, isMultiSelect);
      },
    [drilldown],
  );

  const makeIsSelected = useCallback(
    (dimension: DrillDimension, keyField: string) =>
      (row: TableRow, _index: number): boolean => {
        if (!drilldown) return false;
        const value = row[keyField] as string;
        return drilldown.isSelected(dimension, value);
      },
    [drilldown],
  );

  // Handlers memorizados por tabela
  const dh = useMemo(
    () => ({
      sourceMedium: {
        onClick: makeRowClick("source_medium", "source_medium"),
        isSelected: makeIsSelected("source_medium", "source_medium"),
      },
      campaign: {
        onClick: makeRowClick("campaign", "campaign"),
        isSelected: makeIsSelected("campaign", "campaign"),
      },
      region: {
        onClick: makeRowClick("region", "estado"),
        isSelected: makeIsSelected("region", "estado"),
      },
      city: {
        onClick: makeRowClick("city", "cidade"),
        isSelected: makeIsSelected("city", "cidade"),
      },
      device: {
        onClick: makeRowClick("device", "dispositivo"),
        isSelected: makeIsSelected("device", "dispositivo"),
      },
      channelGroup: {
        onClick: makeRowClick("channel_group", "grupo"),
        isSelected: makeIsSelected("channel_group", "grupo"),
      },
      age: {
        onClick: makeRowClick("age", "idade"),
        isSelected: makeIsSelected("age", "idade"),
      },
      gender: {
        onClick: makeRowClick("gender", "genero"),
        isSelected: makeIsSelected("gender", "genero"),
      },
    }),
    [makeRowClick, makeIsSelected],
  );

  const hasDrilldown = !!drilldown;

  // UseEffect separado para carregar APENAS os KPIs dos cards
  useEffect(() => {
    const loadKpis = async () => {
      try {
        setLoadingKpis(true);
        setErroKpis(false);
        const customerUuid =
          selectedAccount?.customer_uuid ||
          localStorage.getItem("active_customer_uuid");
        if (!customerUuid) {
          setKpis(null);
          setLoadingKpis(false);
          return;
        }
        const accountId = selectedAccount?.account_id;
        const start = formatDateToISO(filterState.dateRange.startDate);
        const end = formatDateToISO(filterState.dateRange.endDate);

        const { data: kpisData } =
          await getGoogleAnalyticsConversionRateCardsKpisApiV1GoogleAnalyticsConversionRateCardsKpisGet(
            {
              query: {
                start_date: start,
                end_date: end,
                comparison: "previous_period",
                customer_uuid: customerUuid,
                account_id: accountId || undefined,
                // <M-118> A busca por produto: os oito cards passam a ser os das sessões
                // que tocaram os itens que a tabela PRODUTOS lista. Até aqui o effect
                // recarregava os cards a cada busca sem mandar o nome, e eles ficavam no
                // total da loja inteira.
                item_name: itemNameApplied,
                item_name_operator: itemNameOperatorApplied,
                // <M-209> Os oito cards do topo passam a honrar o recorte.
                ...drilldownExtraParams,
              },
              throwOnError: true,
            },
          );
        setKpis(kpisData || null);
      } catch (e) {
        console.error("Erro ao carregar KPIs de Conversão:", e);
        setKpis(null);
        setErroKpis(true);
      } finally {
        setLoadingKpis(false);
      }
    };
    loadKpis();
  }, [
    selectedAccount,
    filterState.dateRange.startDate,
    filterState.dateRange.endDate,
    itemNameApplied,
    itemNameOperatorApplied,
    campaignFiltersNonce,
    drilldownExtraParams,
  ]);

  // Aplicar filtro com debounce (evita spam de requests ao digitar)
  useEffect(() => {
    const t = window.setTimeout(() => {
      const v = itemNameQuery.trim();
      setItemNameApplied(v.length > 0 ? v : undefined);
      setItemNameOperatorApplied(itemNameOperator);
    }, 450);
    return () => window.clearTimeout(t);
  }, [itemNameQuery, itemNameOperator]);

  // Quando o filtro de item muda, resetar paginações para evitar páginas inválidas
  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setProductsPage(1);
    setSourcesPage(1);
    setCampaignsPage(1);
    setStatesPage(1);
    setCitiesPage(1);
    setChannelsPage(1);
    setAgesPage(1);
    setGendersPage(1);
  }, [itemNameApplied]);

  // UseEffect para carregar demais dados (gráficos, tabelas exceto Geo/Channels)
  useEffect(() => {
    const load = async () => {
      try {
        setLoading(true);
        // Usar customer_uuid do selectedAccount ou fallback para localStorage
        const customerUuid =
          selectedAccount?.customer_uuid ||
          localStorage.getItem("active_customer_uuid");
        console.log("🔍 [ConversionRate] Customer UUID:", customerUuid);

        if (!customerUuid) {
          console.warn("⚠️ [ConversionRate] Nenhum customer_uuid encontrado");
          setSeries([]);
          setProducts(null);
          setSources(null);
          setCampaigns(null);
          setDemographics(null);
          setLoading(false);
          return;
        }
        // Usar account_id do selectedAccount
        const accountId = selectedAccount?.account_id;
        const start = formatDateToISO(filterState.dateRange.startDate);
        const end = formatDateToISO(filterState.dateRange.endDate);

        console.log("📊 [ConversionRate] Carregando dados:", {
          customerUuid,
          accountId,
          start,
          end,
          productsPage,
        });

        const data = (
          await getConversionRateFullApiV1ConversionRateFullGet({
            query: {
              start_date: start,
              end_date: end,
              comparison: "previous_period",
              customer_uuid: customerUuid,
              account_id: accountId || undefined,
              products_page: productsPage,
              sources_page: sourcesPage,
              campaigns_page: campaignsPage,
              page_size: 10,
              item_name: itemNameApplied,
              item_name_operator: itemNameOperatorApplied,
              // <M-209> O recorte **inteiro**: esta resposta serve cinco blocos, e quem tira
              // a dimensão de cada tabela que publica rótulo é o servidor.
              ...drilldownExtraParams,
            },
          })
        ).data;
        // Buscar campanhas UTM usando novo endpoint dedicado com cache DB/GA4 fallback
        const utmCampaigns = (
          await getConversionRateUtmCampaignsApiV1ConversionRateUtmCampaignsGet(
            {
              query: {
                start_date: start,
                end_date: end,
                comparison: "previous_period",
                customer_uuid: customerUuid,
                account_id: accountId || undefined,
                page: campaignsPage,
                page_size: 10,
                // <M-209> Sem `filter_campaign`: é a dimensão que esta tabela desenha.
                ...recorteExceto("campaign"),
              },
            },
          )
        ).data;

        // Buscar produtos usando o mesmo endpoint da página Produtos
        const productsData = (
          await getProductsRevenueTrendApiV1ProductsRevenueTrendGet({
            query: {
              start_date: start,
              end_date: end,
              comparison: "previous_period",
              customer_uuid: customerUuid,
              limit: 5,
              page: productsPage,
              page_size: 10,
              item_name: itemNameApplied,
              item_name_operator: itemNameOperatorApplied,
              // <M-209> `filter_product` não é mandado por rota nenhuma desta página, então
              // não há própria dimensão a tirar aqui.
              ...drilldownExtraParams,
            },
          })
        ).data;

        console.log("✅ [ConversionRate] Dados recebidos:", {
          series: data?.chart?.series?.length || 0,
          products: productsData?.items?.length || 0,
          sources: data?.sources?.items?.length || 0,
          campaigns: data?.campaigns?.items?.length || 0,
        });

        const demo = (
          await getConversionRateDemographicsV2ApiV1ConversionRateDemographicsV2Get(
            {
              query: {
                start_date: start,
                end_date: end,
                comparison: "previous_period",
                customer_uuid: customerUuid,
                account_id: accountId || undefined,
                page: agesPage,
                page_size: 10,
                // <M-209> O recorte inteiro: a resposta serve os blocos de idade e de
                // gênero, e cada um tira a própria dimensão do lado do servidor.
                ...drilldownExtraParams,
              },
            },
          )
        ).data;
        setSeries(data?.chart?.series || []);
        setProducts(
          productsData
            ? {
                items: productsData.items,
                pagination: productsData.pagination,
                // <M-227> `null` quando a tabela está vazia — é o invariante da rota.
                totalRow: (productsData.total_row ?? null) as TableRow | null,
              }
            : null,
        );
        setSources(
          data?.sources
            ? {
                items: data.sources.items,
                pagination: data.sources.pagination,
                // <M-228> `null` quando a tabela está vazia — é o invariante da rota.
                totalRow: (data.sources.total_row ?? null) as TableRow | null,
              }
            : null,
        );
        setCampaigns(
          utmCampaigns
            ? {
                items: utmCampaigns.items,
                pagination: utmCampaigns.pagination,
                // <M-229> O invariante "rodapé ⟺ conjunto não vazio", que esta rota não
                // cumpre sozinha; ver `rodapeDoConjunto`.
                totalRow: rodapeDoConjunto(
                  utmCampaigns.total_row,
                  utmCampaigns.pagination?.total,
                ),
              }
            : null,
        );
        setDemographics(demo || null);
      } catch (e) {
        console.error("❌ [ConversionRate] Erro ao carregar dados:", e);
        setSeries([]);
        setProducts(null);
        setSources(null);
        setCampaigns(null);
        setDemographics(null);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [
    selectedAccount,
    filterState.dateRange.startDate,
    filterState.dateRange.endDate,
    filterState.period,
    filterState.comparison,
    filterState.customDateRange,
    productsPage,
    sourcesPage,
    campaignsPage,
    agesPage,
    gendersPage,
    itemNameApplied,
    itemNameOperatorApplied,
    campaignFiltersNonce,
    drilldownExtraParams,
    recorteExceto,
  ]);

  // Série diária para o gráfico de TX Conversão (período atual + anterior)
  useEffect(() => {
    const loadBarTimeline = async () => {
      try {
        const customerUuid =
          selectedAccount?.customer_uuid ||
          localStorage.getItem("active_customer_uuid");
        if (!customerUuid) {
          setBarTimeline([]);
          setBarTimelinePrev([]);
          return;
        }
        const accountId = selectedAccount?.account_id;
        const start = formatDateToISO(filterState.dateRange.startDate);
        const end = formatDateToISO(filterState.dateRange.endDate);
        const comparison =
          filterState.comparison?.type === "previousYear"
            ? "previous_year"
            : "previous_period";

        // Período atual
        const timelineData = (
          await getAcquisitionTimelineApiV1AcquisitionTimelineGet({
            query: {
              start_date: start,
              end_date: end,
              comparison,
              customer_uuid: customerUuid,
              account_id: accountId || undefined,
              item_name: itemNameApplied,
              item_name_operator: itemNameOperatorApplied,
              // <M-209> A rota já declarava a família no congelado, e era a única da página
              // cujo filtro chegava a alguma coisa — mas só o primeiro preenchido valia, e
              // ela nem os recebia daqui. Os oito valem agora, juntos.
              ...drilldownExtraParams,
            },
          })
        ).data;
        setBarTimeline(
          Array.isArray(timelineData?.series) ? timelineData.series : [],
        );

        // Período anterior (mesma duração, deslocado para trás)
        const startD = new Date(start + "T00:00:00");
        const endD = new Date(end + "T00:00:00");
        const diffMs = endD.getTime() - startD.getTime();
        const diffDays = Math.round(diffMs / (1000 * 60 * 60 * 24)) + 1;
        const prevEndD = new Date(startD.getTime() - 1000 * 60 * 60 * 24); // dia antes de start
        const prevStartD = new Date(
          prevEndD.getTime() - (diffDays - 1) * 1000 * 60 * 60 * 24,
        );
        const prevStart = prevStartD.toISOString().slice(0, 10);
        const prevEnd = prevEndD.toISOString().slice(0, 10);

        const prevData = (
          await getAcquisitionTimelineApiV1AcquisitionTimelineGet({
            query: {
              start_date: prevStart,
              end_date: prevEnd,
              comparison,
              customer_uuid: customerUuid,
              account_id: accountId || undefined,
              item_name: itemNameApplied,
              item_name_operator: itemNameOperatorApplied,
              ...drilldownExtraParams,
            },
          })
        ).data;
        setBarTimelinePrev(
          Array.isArray(prevData?.series) ? prevData.series : [],
        );
      } catch (e) {
        console.error(
          "❌ [ConversionRate] Erro ao carregar timeline (acquisition/timeline):",
          e,
        );
        setBarTimeline([]);
        setBarTimelinePrev([]);
      }
    };

    loadBarTimeline();
  }, [
    selectedAccount,
    filterState.dateRange.startDate,
    filterState.dateRange.endDate,
    filterState.comparison,
    itemNameApplied,
    itemNameOperatorApplied,
    campaignFiltersNonce,
    drilldownExtraParams,
  ]);

  // UseEffect dedicado para carregar os cards de GEO e Canais (Estados, Cidades, Dispositivos, Grupo de Canais)
  useEffect(() => {
    const loadGeoChannels = async () => {
      try {
        setLoadingGeo(true);
        const customerUuid =
          selectedAccount?.customer_uuid ||
          localStorage.getItem("active_customer_uuid");
        if (!customerUuid) {
          setStatesData(null);
          setCitiesData(null);
          setDevicesData(null);
          setChannelsData(null);
          return;
        }
        const accountId = selectedAccount?.account_id;
        const start = formatDateToISO(filterState.dateRange.startDate);
        const end = formatDateToISO(filterState.dateRange.endDate);

        const [statesRes, citiesRes, devicesRes, channelsRes] =
          await Promise.all([
            getRegionsSalesByStateApiV1RegionsSalesByStateGet({
              query: {
                start_date: start,
                end_date: end,
                comparison: "previous_period",
                customer_uuid: customerUuid,
                account_id: accountId || undefined,
                page: statesPage,
                page_size: 10,
                // <M-209> Sem `filter_region`: é a dimensão que esta tabela desenha.
                ...recorteExceto("region"),
              },
            }),
            getRegionsSalesByCityApiV1RegionsSalesByCityGet({
              query: {
                start_date: start,
                end_date: end,
                comparison: "previous_period",
                customer_uuid: customerUuid,
                account_id: accountId || undefined,
                page: citiesPage,
                page_size: 10,
                // <M-209> Sem `filter_city`, pelo mesmo motivo.
                ...recorteExceto("city"),
              },
            }),
            getDevicesSalesApiV1DevicesSalesGet({
              query: {
                start_date: start,
                end_date: end,
                comparison: "previous_period",
                customer_uuid: customerUuid,
                account_id: accountId || undefined,
                // <M-209> Sem `filter_device`, pelo mesmo motivo.
                ...recorteExceto("device"),
              },
            }),
            getAcquisitionChannelGroupingApiV1AcquisitionChannelGroupingGet({
              query: {
                start_date: start,
                end_date: end,
                comparison: "previous_period",
                customer_uuid: customerUuid,
                property_id: accountId || undefined,
                page: channelsPage,
                page_size: 10,
                // <M-209> Sem `filter_channel_group`, pelo mesmo motivo.
                ...recorteExceto("channel_group"),
              },
            }),
          ]);

        setStatesData({
          items: statesRes.data?.items ?? [],
          pagination: statesRes.data?.pagination
            ? {
                page: statesRes.data.pagination.page,
                page_size: statesRes.data.pagination.page_size,
                total: statesRes.data.pagination.total,
              }
            : undefined,
          // <M-230> `total_row` é obrigatório nesta rota e sai zerado sobre conjunto vazio;
          // o invariante das irmãs passa a valer aqui — ver `rodapeDoConjunto`.
          totalRow: rodapeDoConjunto(
            statesRes.data?.total_row,
            statesRes.data?.pagination?.total,
          ),
        });
        setCitiesData({
          items: citiesRes.data?.items ?? [],
          pagination: citiesRes.data?.pagination
            ? {
                page: citiesRes.data.pagination.page,
                page_size: citiesRes.data.pagination.page_size,
                total: citiesRes.data.pagination.total,
              }
            : undefined,
          // <M-230> Idem — a mesma rota, na outra dimensão.
          totalRow: rodapeDoConjunto(
            citiesRes.data?.total_row,
            citiesRes.data?.pagination?.total,
          ),
        });
        const deviceItems = devicesRes.data?.items ?? [];
        const totalSessions = deviceItems.reduce(
          (sum, d) => sum + d.sessions,
          0,
        );
        setDevicesData({
          table: deviceItems,
          // <M-230> Esta rota não pagina: o conjunto é a própria lista de dispositivos.
          totalRow: rodapeDoConjunto(
            devicesRes.data?.total_row,
            deviceItems.length,
          ),
          chart: deviceItems.map((d) => ({
            device: d.device,
            sessions: d.sessions,
            percentual:
              totalSessions > 0
                ? Math.round((d.sessions / totalSessions) * 100)
                : 0,
          })),
        });
        setChannelsData({
          items: channelsRes.data?.items ?? [],
          pagination: channelsRes.data?.pagination
            ? {
                page: channelsRes.data.pagination.page,
                page_size: channelsRes.data.pagination.page_size,
                total: channelsRes.data.pagination.total,
              }
            : undefined,
          // <M-230> `total_row` também é obrigatório aqui, e também sai zerado sem linha.
          totalRow: rodapeDoConjunto(
            channelsRes.data?.total_row,
            channelsRes.data?.pagination?.total,
          ),
        });
      } catch (e) {
        console.error("❌ [ConversionRate] Erro ao carregar geoChannels:", e);
        setStatesData(null);
        setCitiesData(null);
        setDevicesData(null);
        setChannelsData(null);
      } finally {
        setLoadingGeo(false);
      }
    };
    loadGeoChannels();
  }, [
    selectedAccount,
    filterState.dateRange.startDate,
    filterState.dateRange.endDate,
    filterState.period,
    filterState.comparison,
    filterState.customDateRange,
    statesPage,
    citiesPage,
    channelsPage,
    itemNameApplied,
    itemNameOperatorApplied,
    campaignFiltersNonce,
    drilldownExtraParams,
    recorteExceto,
  ]);

  return (
    <div className="p-6 space-y-6 page-background">
      <div>
        <h1 className="text-3xl font-bold tracking-tight">Taxa de Conversão</h1>
        <p className="text-muted-foreground">
          Análise detalhada das métricas de conversão e engajamento do seu
          e-commerce
        </p>
      </div>

      <GlobalFilter
        rightSlot={
          <CampaignFiltersControl
            customerUuid={customerUuid}
            accountId={selectedAccount?.account_id}
            onSaved={handleCampaignFiltersSaved}
          />
        }
      />

      {/* Filtros Ativos de Cross-Filtering */}
      <ActiveFiltersDisplay />

      {/* Filtro "Nome do Item" movido para ao lado do gráfico */}

      {/* PRIMEIRA LINHA: CARDS PRINCIPAIS */}
      <div className="grid gap-6 grid-cols-4">
        {/* Receita Total */}
        <Card className="card-kpi-primary">
          <CardHeader>
            <CardTitle>
              <span className="inline-flex items-center gap-1">
                <span className="truncate">Receita Total</span>
                <HelpTooltip
                  content={getKpiTooltip("Receita Total")}
                  triggerClassName="h-3.5 w-3.5 text-[9px]"
                />
              </span>
            </CardTitle>
          </CardHeader>
          <CardContent>
            {loadingKpis ? (
              <CarregandoValor className="text-2xl" />
            ) : (
              <>
                <div className="min-w-0">
                  <EstadoDoValor
                    carregando={false}
                    disponivel={
                      kpis?.total_revenue !== null &&
                      kpis?.total_revenue !== undefined
                    }
                    fontes={FONTES_DOS_CARDS}
                    erro={erroKpis}
                    className="text-2xl font-light text-muted-foreground"
                  >
                    <KpiNumber
                      value={Number(kpis?.total_revenue)}
                      format={getKpiFormat(
                        String(kpis?.total_revenue_data_type ?? "CURRENCY"),
                      )}
                      className="font-light text-foreground"
                      fontPx={18}
                      ariaLabel="Receita total"
                    />
                  </EstadoDoValor>
                </div>
                <div className="flex items-center text-xs text-muted-foreground mt-2">
                  {kpis &&
                  typeof kpis.total_revenue_change_percentage === "number" ? (
                    <TrendIndicator
                      value={kpis.total_revenue_change_percentage}
                    />
                  ) : (
                    <ValorIndisponivel
                      fontes={FONTES_DOS_CARDS}
                      erro={erroKpis}
                      className="text-xs text-muted-foreground"
                    />
                  )}
                </div>
              </>
            )}
          </CardContent>
        </Card>

        {/* Pedidos */}
        <Card className="card-kpi-primary">
          <CardHeader>
            <CardTitle>
              <span className="inline-flex items-center gap-1">
                <span className="truncate">Pedidos</span>
                <HelpTooltip
                  content={getKpiTooltip("Pedidos")}
                  triggerClassName="h-3.5 w-3.5 text-[9px]"
                />
              </span>
            </CardTitle>
          </CardHeader>
          <CardContent>
            {loadingKpis ? (
              <CarregandoValor className="text-2xl" />
            ) : (
              <>
                <div className="min-w-0">
                  <EstadoDoValor
                    carregando={false}
                    disponivel={
                      kpis?.orders !== null && kpis?.orders !== undefined
                    }
                    fontes={FONTES_DOS_CARDS}
                    erro={erroKpis}
                    className="text-2xl font-light text-muted-foreground"
                  >
                    <KpiNumber
                      value={Number(kpis?.orders)}
                      format={getKpiFormat(
                        String(kpis?.orders_data_type ?? "NUMBER"),
                      )}
                      className="font-light text-foreground"
                      fontPx={18}
                      ariaLabel="Pedidos"
                    />
                  </EstadoDoValor>
                </div>
                <div className="flex items-center text-xs text-muted-foreground mt-2">
                  {kpis && typeof kpis.orders_change_percentage === "number" ? (
                    <TrendIndicator value={kpis.orders_change_percentage} />
                  ) : (
                    <ValorIndisponivel
                      fontes={FONTES_DOS_CARDS}
                      erro={erroKpis}
                      className="text-xs text-muted-foreground"
                    />
                  )}
                </div>
              </>
            )}
          </CardContent>
        </Card>

        {/* Ticket Médio */}
        <Card className="card-kpi-primary">
          <CardHeader>
            <CardTitle>
              <span className="inline-flex items-center gap-1">
                <span className="truncate">Ticket Médio</span>
                <HelpTooltip
                  content={getKpiTooltip("Ticket Médio")}
                  triggerClassName="h-3.5 w-3.5 text-[9px]"
                />
              </span>
            </CardTitle>
          </CardHeader>
          <CardContent>
            {loadingKpis ? (
              <CarregandoValor className="text-2xl" />
            ) : (
              <>
                <div className="flex flex-col items-start min-w-0">
                  <div className="min-w-0 flex-1">
                    <EstadoDoValor
                      carregando={false}
                      disponivel={
                        kpis?.average_order_value !== null &&
                        kpis?.average_order_value !== undefined
                      }
                      fontes={FONTES_DOS_CARDS}
                      erro={erroKpis}
                      className="text-2xl font-light text-muted-foreground"
                    >
                      <KpiNumber
                        value={Number(kpis?.average_order_value)}
                        format={getKpiFormat(
                          String(
                            kpis?.average_order_value_data_type ?? "CURRENCY",
                          ),
                        )}
                        className="font-light text-foreground"
                        fontPx={18}
                        ariaLabel="Ticket médio"
                      />
                    </EstadoDoValor>
                  </div>
                  {comparisonEnabled &&
                    (kpis &&
                    typeof kpis.average_order_value_change_percentage ===
                      "number" ? (
                      <TrendIndicator
                        value={kpis.average_order_value_change_percentage}
                      />
                    ) : (
                      <ValorIndisponivel
                        fontes={FONTES_DOS_CARDS}
                        erro={erroKpis}
                        className="text-xs text-muted-foreground"
                      />
                    ))}
                </div>
              </>
            )}
          </CardContent>
        </Card>

        {/* Taxa de Conversão */}
        <Card className="card-kpi-primary">
          <CardHeader>
            <CardTitle>
              <span className="inline-flex items-center gap-1">
                <span className="truncate">Taxa Conversão</span>
                <HelpTooltip
                  content={getKpiTooltip("Taxa Conversão")}
                  triggerClassName="h-3.5 w-3.5 text-[9px]"
                />
              </span>
            </CardTitle>
          </CardHeader>
          <CardContent>
            {loadingKpis ? (
              <CarregandoValor className="text-2xl" />
            ) : (
              <>
                <div className="flex flex-col items-start min-w-0">
                  <div className="min-w-0 flex-1">
                    <EstadoDoValor
                      carregando={false}
                      disponivel={
                        kpis?.conversion_rate !== null &&
                        kpis?.conversion_rate !== undefined
                      }
                      fontes={FONTES_DOS_CARDS}
                      erro={erroKpis}
                      className="text-2xl font-light text-muted-foreground"
                    >
                      <KpiNumber
                        value={Number(kpis?.conversion_rate)}
                        format={getKpiFormat(
                          String(kpis?.conversion_rate_data_type ?? "PERCENT"),
                        )}
                        className="font-light text-foreground"
                        fontPx={18}
                        ariaLabel="Taxa de conversão"
                      />
                    </EstadoDoValor>
                  </div>
                  {comparisonEnabled &&
                    (kpis &&
                    typeof kpis.conversion_rate_change_percentage ===
                      "number" ? (
                      <TrendIndicator
                        value={kpis.conversion_rate_change_percentage}
                      />
                    ) : (
                      <ValorIndisponivel
                        fontes={FONTES_DOS_CARDS}
                        erro={erroKpis}
                        className="text-xs text-muted-foreground"
                      />
                    ))}
                </div>
              </>
            )}
          </CardContent>
        </Card>
      </div>

      {/* SEGUNDA LINHA: CARDS DE USUÁRIOS E SESSÕES */}
      <div className="grid gap-6 grid-cols-4">
        {/* Total de Usuários */}
        <Card className="card-kpi-primary">
          <CardHeader>
            <CardTitle>
              <span className="inline-flex items-center gap-1">
                <span className="truncate">Total Usuários</span>
                <HelpTooltip
                  content={getKpiTooltip("Usuários")}
                  triggerClassName="h-3.5 w-3.5 text-[9px]"
                />
              </span>
            </CardTitle>
          </CardHeader>
          <CardContent>
            {loadingKpis ? (
              <CarregandoValor className="text-2xl" />
            ) : (
              <>
                <div className="flex flex-col items-start min-w-0">
                  <div className="min-w-0 flex-1">
                    <EstadoDoValor
                      carregando={false}
                      disponivel={
                        kpis?.total_users !== null &&
                        kpis?.total_users !== undefined
                      }
                      fontes={FONTES_DOS_CARDS}
                      erro={erroKpis}
                      className="text-2xl font-light text-muted-foreground"
                    >
                      <KpiNumber
                        value={Number(kpis?.total_users)}
                        format={getKpiFormat(
                          String(kpis?.total_users_data_type ?? "NUMBER"),
                        )}
                        className="font-light text-foreground"
                        fontPx={18}
                        ariaLabel="Total de usuários"
                      />
                    </EstadoDoValor>
                  </div>
                  {comparisonEnabled &&
                    (kpis &&
                    typeof kpis.total_users_change_percentage === "number" ? (
                      <TrendIndicator
                        value={kpis.total_users_change_percentage}
                      />
                    ) : (
                      <ValorIndisponivel
                        fontes={FONTES_DOS_CARDS}
                        erro={erroKpis}
                        className="text-xs text-muted-foreground"
                      />
                    ))}
                </div>
              </>
            )}
          </CardContent>
        </Card>

        {/* Sessões */}
        <Card className="card-kpi-primary">
          <CardHeader>
            <CardTitle>
              <span className="inline-flex items-center gap-1">
                <span className="truncate">Sessões</span>
                <HelpTooltip
                  content={getKpiTooltip("Sessões")}
                  triggerClassName="h-3.5 w-3.5 text-[9px]"
                />
              </span>
            </CardTitle>
          </CardHeader>
          <CardContent>
            {loadingKpis ? (
              <CarregandoValor className="text-2xl" />
            ) : (
              <>
                <div className="flex flex-col items-start min-w-0">
                  <div className="min-w-0 flex-1">
                    <EstadoDoValor
                      carregando={false}
                      disponivel={
                        kpis?.sessions !== null && kpis?.sessions !== undefined
                      }
                      fontes={FONTES_DOS_CARDS}
                      erro={erroKpis}
                      className="text-2xl font-light text-muted-foreground"
                    >
                      <KpiNumber
                        value={Number(kpis?.sessions)}
                        format={getKpiFormat(
                          String(kpis?.sessions_data_type ?? "NUMBER"),
                        )}
                        className="font-light text-foreground"
                        fontPx={18}
                        ariaLabel="Sessões"
                      />
                    </EstadoDoValor>
                  </div>
                  {comparisonEnabled &&
                    (kpis &&
                    typeof kpis.sessions_change_percentage === "number" ? (
                      <TrendIndicator value={kpis.sessions_change_percentage} />
                    ) : (
                      <ValorIndisponivel
                        fontes={FONTES_DOS_CARDS}
                        erro={erroKpis}
                        className="text-xs text-muted-foreground"
                      />
                    ))}
                </div>
              </>
            )}
          </CardContent>
        </Card>

        {/* Sessões Engajadas */}
        <Card className="card-kpi-primary">
          <CardHeader>
            <CardTitle>
              <span className="inline-flex items-center gap-1">
                <span className="truncate">Sessões Engajadas</span>
                <HelpTooltip
                  content={getKpiTooltip("Sessões Engajadas")}
                  triggerClassName="h-3.5 w-3.5 text-[9px]"
                />
              </span>
            </CardTitle>
          </CardHeader>
          <CardContent>
            {loadingKpis ? (
              <CarregandoValor className="text-2xl" />
            ) : (
              <>
                <div className="flex flex-col items-start min-w-0">
                  <div className="min-w-0 flex-1">
                    <EstadoDoValor
                      carregando={false}
                      disponivel={
                        kpis?.engaged_sessions !== null &&
                        kpis?.engaged_sessions !== undefined
                      }
                      fontes={FONTES_DOS_CARDS}
                      erro={erroKpis}
                      className="text-2xl font-light text-muted-foreground"
                    >
                      <KpiNumber
                        value={Number(kpis?.engaged_sessions)}
                        format={getKpiFormat(
                          String(kpis?.engaged_sessions_data_type ?? "NUMBER"),
                        )}
                        className="font-light text-foreground"
                        fontPx={18}
                        ariaLabel="Sessões engajadas"
                      />
                    </EstadoDoValor>
                  </div>
                  {comparisonEnabled &&
                    (kpis &&
                    typeof kpis.engaged_sessions_change_percentage ===
                      "number" ? (
                      <TrendIndicator
                        value={kpis.engaged_sessions_change_percentage}
                      />
                    ) : (
                      <ValorIndisponivel
                        fontes={FONTES_DOS_CARDS}
                        erro={erroKpis}
                        className="text-xs text-muted-foreground"
                      />
                    ))}
                </div>
              </>
            )}
          </CardContent>
        </Card>

        {/* TX Engajamento */}
        <Card className="card-kpi-primary">
          <CardHeader>
            <CardTitle>
              <span className="inline-flex items-center gap-1">
                <span className="truncate">Tx Engajamento</span>
                <HelpTooltip
                  content={getKpiTooltip("Taxa Engajamento")}
                  triggerClassName="h-3.5 w-3.5 text-[9px]"
                />
              </span>
            </CardTitle>
          </CardHeader>
          <CardContent>
            {loadingKpis ? (
              <CarregandoValor className="text-2xl" />
            ) : (
              <>
                <div className="flex flex-col items-start min-w-0">
                  <div className="min-w-0 flex-1">
                    <EstadoDoValor
                      carregando={false}
                      disponivel={
                        kpis?.engagement_rate !== null &&
                        kpis?.engagement_rate !== undefined
                      }
                      fontes={FONTES_DOS_CARDS}
                      erro={erroKpis}
                      className="text-2xl font-light text-muted-foreground"
                    >
                      <KpiNumber
                        value={Number(kpis?.engagement_rate)}
                        format={getKpiFormat(
                          String(kpis?.engagement_rate_data_type ?? "PERCENT"),
                        )}
                        className="font-light text-foreground"
                        fontPx={18}
                        ariaLabel="Taxa de engajamento"
                      />
                    </EstadoDoValor>
                  </div>
                  {comparisonEnabled &&
                    (kpis &&
                    typeof kpis.engagement_rate_change_percentage ===
                      "number" ? (
                      <TrendIndicator
                        value={kpis.engagement_rate_change_percentage}
                      />
                    ) : (
                      <ValorIndisponivel
                        fontes={FONTES_DOS_CARDS}
                        erro={erroKpis}
                        className="text-xs text-muted-foreground"
                      />
                    ))}
                </div>
              </>
            )}
          </CardContent>
        </Card>
      </div>

      {/* SEGUNDA LINHA: GRÁFICO DE TX CONVERSÃO + FILTRO NOME DO ITEM */}
      <div className="grid gap-6 lg:grid-cols-4">
        {/* Gráfico de linhas - Taxa de Conversão */}
        <Card className="card-kpi-secondary lg:col-span-3">
          <CardHeader>
            <CardTitle>TAXA DE CONVERSÃO</CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="space-y-3">
                <Skeleton className="h-8 w-full" />
                <Skeleton className="h-8 w-full" />
                <Skeleton className="h-8 w-full" />
                <Skeleton className="h-8 w-full" />
              </div>
            ) : barTimeline.length === 0 ? (
              <div className="flex items-center justify-center h-[200px] text-muted-foreground">
                Nenhum dado disponível para o período selecionado
              </div>
            ) : (
              (() => {
                const startD = new Date(
                  formatDateToISO(filterState.dateRange.startDate) +
                    "T00:00:00",
                );
                const endD = new Date(
                  formatDateToISO(filterState.dateRange.endDate) + "T00:00:00",
                );
                const diffDays =
                  Math.round(
                    (endD.getTime() - startD.getTime()) / (1000 * 60 * 60 * 24),
                  ) + 1;

                const data = barTimeline.map((p: TableRow, idx: number) => {
                  const row: TableRow = {
                    data: formatDateBR(p.date_label || p.date),
                    "Taxa de Conversão": Number(p.conversion_rate || 0),
                  };
                  // Alinhar dado do período anterior por posição (índice)
                  if (barTimelinePrev[idx]) {
                    row[`Taxa de Conversão (${diffDays} dias anteriores)`] =
                      Number(barTimelinePrev[idx].conversion_rate || 0);
                  }
                  return row;
                });

                const categories = [
                  "Taxa de Conversão",
                  `Taxa de Conversão (${diffDays} dias anteriores)`,
                ];

                return (
                  <ScrollableChart>
                    <TremorLineChart
                      className="h-72"
                      data={data}
                      index="data"
                      categories={categories}
                      colors={["cyan", "amber"]}
                      valueFormatter={(v: number) =>
                        `${Number(v || 0).toFixed(2)}%`
                      }
                      showLegend={true}
                      showGridLines={true}
                      showAnimation={true}
                    />
                  </ScrollableChart>
                );
              })()
            )}
          </CardContent>
        </Card>

        {/* Filtro Nome do Item (ao lado do gráfico) */}
        <Card className="card-kpi-secondary lg:col-span-1">
          <CardContent className="pt-6">
            <div className="flex flex-col gap-3">
              <div className="text-sm font-medium">Nome do item</div>
              <Select
                value={itemNameOperator}
                onValueChange={(v: ItemNameOperator) => setItemNameOperator(v)}
              >
                <SelectTrigger className="w-full">
                  <SelectValue placeholder="Operador" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="equals">Igual a</SelectItem>
                  <SelectItem value="contains">Contém</SelectItem>
                  <SelectItem value="starts_with">Começa com</SelectItem>
                  <SelectItem value="regex">Regex</SelectItem>
                  <SelectItem value="in">Em</SelectItem>
                </SelectContent>
              </Select>
              <Input
                value={itemNameQuery}
                onChange={(e) => setItemNameQuery(e.target.value)}
                placeholder="Insira um valor"
              />
              {itemNameQuery.trim().length > 0 && (
                <Button
                  type="button"
                  variant="secondary"
                  size="sm"
                  onClick={() => setItemNameQuery("")}
                >
                  Limpar
                </Button>
              )}
              {itemNameApplied && (
                <div className="text-xs text-muted-foreground">
                  Filtrando por:{" "}
                  <span className="font-medium text-foreground">
                    {itemNameApplied}
                  </span>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* TERCEIRA LINHA: TABELA DE CATEGORIAS */}
      {/* <M-212> Sem `drilldownEnabled`: o padrão é `false`, e esta página não honra
          recorte de item — ver `FILTROS_DE_ESCOPO_DE_ITEM`. */}
      <CategoriasMaisVendidasTable
        itemName={itemNameApplied}
        itemNameOperator={itemNameOperatorApplied}
      />

      {/* QUARTA LINHA: PRODUTOS MAIS VENDIDOS */}
      {/* A tabela fica montada durante a recarga, como as outras oito da página. */}
      <ProdutosMaisVendidosTable
        data={(products?.items || []).map((item: TableRow) => ({
          item_name: item.item_name || item.product_name,
          items_viewed: item.items_viewed || 0,
          items_viewed_change_percentage:
            item.items_viewed_change_percentage || 0,
          sessions: item.sessions || 0,
          sessions_change_percentage: item.sessions_change_percentage || 0,
          session_conversion_rate: item.session_conversion_rate || 0,
          session_conversion_rate_change_percentage:
            item.session_conversion_rate_change_percentage || 0,
          items_added_to_cart: item.items_added_to_cart || 0,
          items_added_to_cart_change_percentage:
            item.items_added_to_cart_change_percentage || 0,
          tx_carrinho: item.tx_carrinho || 0,
          tx_carrinho_change_percentage:
            item.tx_carrinho_change_percentage || 0,
          items_purchased: item.items_purchased || 0,
          items_purchased_change_percentage:
            item.items_purchased_change_percentage || 0,
          tx_conv_itens: item.tx_conv_itens || 0,
          tx_conv_itens_change_percentage:
            item.tx_conv_itens_change_percentage || 0,
          purchase_revenue: item.purchase_revenue || 0,
          purchase_revenue_change_percentage:
            item.purchase_revenue_change_percentage || 0,
        }))}
        total={products?.totalRow ?? undefined}
        pagination={
          products?.pagination
            ? {
                current: products.pagination.page,
                pageSize: products.pagination.page_size,
                total: products.pagination.total,
                onPageChange: (p: number) => setProductsPage(p),
              }
            : undefined
        }
        loading={loading}
      />

      {/* QUINTA LINHA: TABELAS DE CONVERSÃO - PRIMEIRA LINHA (2 COLUNAS) */}
      <div className="grid gap-6 md:grid-cols-1 lg:grid-cols-2">
        <ConversaoPorOrigemMidiaTable
          loading={loading}
          onRowClick={hasDrilldown ? dh.sourceMedium.onClick : undefined}
          isRowSelected={hasDrilldown ? dh.sourceMedium.isSelected : undefined}
          clickableRows={hasDrilldown}
          data={sources?.items || []}
          total={sources?.totalRow ?? undefined}
          pagination={
            sources?.pagination
              ? {
                  current: sources.pagination.page,
                  pageSize: sources.pagination.page_size,
                  total: sources.pagination.total,
                  onPageChange: (p: number) => setSourcesPage(p),
                }
              : undefined
          }
        />
        <ConversaoPorCampanhaTable
          loading={loading}
          onRowClick={hasDrilldown ? dh.campaign.onClick : undefined}
          isRowSelected={hasDrilldown ? dh.campaign.isSelected : undefined}
          clickableRows={hasDrilldown}
          data={campaigns?.items || []}
          total={campaigns?.totalRow ?? undefined}
          pagination={
            campaigns?.pagination
              ? {
                  current: campaigns.pagination.page,
                  pageSize: campaigns.pagination.page_size,
                  total: campaigns.pagination.total,
                  onPageChange: (p: number) => setCampaignsPage(p),
                }
              : undefined
          }
        />
      </div>

      {itemNameApplied ? (
        <Card className="card-kpi-secondary">
          <CardHeader>
            <CardTitle>FILTRO POR ITEM ATIVO</CardTitle>
            <CardDescription className="text-center">
              As análises por região/dispositivo/demografia não se aplicam
              quando filtramos por um item específico.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="text-sm text-muted-foreground text-center">
              Remova o filtro “Nome do item” para visualizar essas seções
              novamente.
            </div>
          </CardContent>
        </Card>
      ) : (
        <>
          {/* SEXTA LINHA: TABELAS DE CONVERSÃO - SEGUNDA LINHA (2 COLUNAS) */}
          <div className="grid gap-6 md:grid-cols-1 lg:grid-cols-2">
            <ConversaoPorEstadoTable
              loading={loadingGeo}
              onRowClick={hasDrilldown ? dh.region.onClick : undefined}
              isRowSelected={hasDrilldown ? dh.region.isSelected : undefined}
              clickableRows={hasDrilldown}
              data={(statesData?.items || []).map(linhaDeEstado)}
              total={
                statesData?.totalRow
                  ? linhaDeEstado(statesData.totalRow)
                  : undefined
              }
              pagination={
                statesData?.pagination
                  ? {
                      current: statesData.pagination.page,
                      pageSize: statesData.pagination.page_size,
                      total: statesData.pagination.total,
                      onPageChange: (p: number) => setStatesPage(p),
                    }
                  : undefined
              }
            />
            <ConversaoPorCidadeTable
              loading={loadingGeo}
              onRowClick={hasDrilldown ? dh.city.onClick : undefined}
              isRowSelected={hasDrilldown ? dh.city.isSelected : undefined}
              clickableRows={hasDrilldown}
              data={(citiesData?.items || []).map(linhaDeCidade)}
              total={
                citiesData?.totalRow
                  ? linhaDeCidade(citiesData.totalRow)
                  : undefined
              }
              pagination={
                citiesData?.pagination
                  ? {
                      current: citiesData.pagination.page,
                      pageSize: citiesData.pagination.page_size,
                      total: citiesData.pagination.total,
                      onPageChange: (p: number) => setCitiesPage(p),
                    }
                  : undefined
              }
            />
          </div>

          {/* SÉTIMA LINHA: GRÁFICO DE DISPOSITIVOS E TABELA DE GRUPO DE CANAIS */}
          <div className="grid gap-6 md:grid-cols-1 lg:grid-cols-2">
            {/* Conversão por dispositivos - Apenas gráfico */}
            <Card className="card-kpi-secondary">
              <CardHeader>
                <CardTitle>CONVERSÃO POR DISPOSITIVOS</CardTitle>
              </CardHeader>
              <CardContent>
                {loadingGeo && !devicesData ? (
                  <CarregandoConteudo />
                ) : (
                  <div className="flex flex-col items-center">
                    <TremorDonutChart
                      className="h-72"
                      data={(devicesData?.chart || []).map((d: TableRow) => ({
                        dispositivo: d.device,
                        percentual: Number(d.percentual || 0),
                      }))}
                      index="dispositivo"
                      category="percentual"
                      colors={["blue", "cyan", "gray"]}
                      valueFormatter={(value) => formatPercent(value)}
                      showLabel={true}
                      showAnimation={true}
                    />
                    <div className="flex flex-col space-y-2 mt-4">
                      {(devicesData?.chart || []).map(
                        (item: TableRow, index: number) => (
                          <div
                            key={
                              item.device ||
                              item.dispositivo ||
                              `device-${index}`
                            }
                            className="flex items-center space-x-2"
                          >
                            <div
                              className="w-3 h-3 rounded-full"
                              style={{
                                backgroundColor:
                                  ["#3b82f6", "#06b6d4", "#6b7280"][index] ??
                                  "#6b7280",
                              }}
                            />
                            <span className="text-sm text-foreground">
                              {item.dispositivo || item.device}
                            </span>
                          </div>
                        ),
                      )}
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Conversão por grupo de canais padrão da sessão */}
            <ConversaoPorGrupoCanaisTable
              loading={loadingGeo}
              onRowClick={hasDrilldown ? dh.channelGroup.onClick : undefined}
              isRowSelected={
                hasDrilldown ? dh.channelGroup.isSelected : undefined
              }
              clickableRows={hasDrilldown}
              data={(channelsData?.items || []).map(linhaDeGrupoDeCanais)}
              total={
                channelsData?.totalRow
                  ? linhaDeGrupoDeCanais(channelsData.totalRow)
                  : undefined
              }
              pagination={
                channelsData?.pagination
                  ? {
                      current: channelsData.pagination.page,
                      pageSize: channelsData.pagination.page_size,
                      total: channelsData.pagination.total,
                      onPageChange: (p: number) => setChannelsPage(p),
                    }
                  : undefined
              }
            />
          </div>

          {/* TABELA DE DISPOSITIVOS FORA DO CARD */}
          <ConversaoPorDispositivosTable
            loading={loadingGeo}
            onRowClick={hasDrilldown ? dh.device.onClick : undefined}
            isRowSelected={hasDrilldown ? dh.device.isSelected : undefined}
            clickableRows={hasDrilldown}
            data={(devicesData?.table || []).map(linhaDeDispositivo)}
            total={
              devicesData?.totalRow
                ? linhaDeDispositivo(devicesData.totalRow)
                : undefined
            }
          />

          {/* OITAVA LINHA: TABELAS DE CONVERSÃO - QUARTA LINHA (2 COLUNAS) */}
          <div className="grid gap-6 md:grid-cols-1 lg:grid-cols-2">
            <ConversaoPorIdadeTable
              loading={loading}
              onRowClick={hasDrilldown ? dh.age.onClick : undefined}
              isRowSelected={hasDrilldown ? dh.age.isSelected : undefined}
              clickableRows={hasDrilldown}
              data={(demographics?.ages?.items || []).map(linhaDeIdade)}
              total={
                demographics?.ages?.total_row
                  ? linhaDeIdade(demographics.ages.total_row)
                  : undefined
              }
              pagination={
                demographics?.ages?.pagination
                  ? {
                      current: demographics.ages.pagination.page,
                      pageSize: demographics.ages.pagination.page_size,
                      total: demographics.ages.pagination.total,
                      onPageChange: (p: number) => setAgesPage(p),
                    }
                  : undefined
              }
            />
            <ConversaoPorGeneroTable
              loading={loading}
              onRowClick={hasDrilldown ? dh.gender.onClick : undefined}
              isRowSelected={hasDrilldown ? dh.gender.isSelected : undefined}
              clickableRows={hasDrilldown}
              data={(demographics?.genders?.items || []).map(linhaDeGenero)}
              total={
                demographics?.genders?.total_row
                  ? linhaDeGenero(demographics.genders.total_row)
                  : undefined
              }
              pagination={
                demographics?.genders?.pagination
                  ? {
                      current: demographics.genders.pagination.page,
                      pageSize: demographics.genders.pagination.page_size,
                      total: demographics.genders.pagination.total,
                      onPageChange: (p: number) => setGendersPage(p),
                    }
                  : undefined
              }
            />
          </div>
        </>
      )}
    </div>
  );
};

const ConversionRatePage: React.FC = () => {
  // <M-210> `product` saiu da lista junto com o clique que o produzia: `itemName` é dimensão
  // de escopo de item, e nenhuma das dez rotas desta página tinha como aplicar o recorte.
  // <M-212> `category` saiu pelo mesmo motivo, quando `CategoriasMaisVendidasTable` ganhou
  // `drilldownEnabled` e esta página passou a desligar o clique dela.
  const hierarchies = useMemo<DrillDimension[][]>(
    () => [
      ["region", "city"],
      ["source_medium"],
      ["campaign"],
      ["device"],
      ["channel_group"],
      ["age"],
      ["gender"],
    ],
    [],
  );

  return (
    <DrilldownProvider hierarchies={hierarchies}>
      <ConversionRatePageContent />
    </DrilldownProvider>
  );
};

export default ConversionRatePage;
